import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from "react"
import moment from "moment-timezone"
import {
  Modal,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  Radio,
  Checkbox,
  Select,
  Divider,
  message,
  Tooltip,
  Skeleton,
  DatePicker,
  Input,
  InputNumber,
  Drawer,
  Space,
} from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faColumns3,
  faClipboardListCheck,
  faListOl,
  faFileExport,
  faFileSpreadsheet,
  faFilePdf,
} from "@fortawesome/pro-regular-svg-icons"
import {
  convertCourse,
  roleMapping,
  gen,
  resolveEventNumber,
  swimminglyApi,
  saveFile,
  download,
} from "./utils"
import generateLineupPDF from "./reports/GenerateLineupPdf"
import generateMeetEntrySummaryPDF from "./reports/GenerateMeetEntrySummaryPdf"
import generateMeetSwimmerSummaryPDF from "./reports/GenerateMeetSwimmerSummaryPdf"
import generateHeatSheetPDF from "./reports/GenerateHeatSheetPdf"

const { Paragraph } = Typography
const { Option, OptGroup } = Select

const tileStyle: React.CSSProperties = {
  minHeight: 96,
  padding: 16,
  width: "100%",
  height: "auto",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  textAlign: "left",
  overflow: "visible",
}

interface ReportTileProps {
  icon: React.ReactNode
  title: string
  description: React.ReactNode
  onClick: () => void
  primary?: boolean
  disabled?: boolean
  isNew?: boolean
}
type ReportKey =
  | "heatSheet"
  | "lineup"
  | "entrySummary"
  | "bestTimes"
  | "exportEntries"
  | "exportMeetEvents"
  | "resultsExport"
  | "resultsExcel"
  | "resultsPdf"
  | "resultsPdfLegacy"
  | "labelsPdfLegacy"
  | "ribbonLabelsPdf"
  | "swimmerSummary"

type RibbonLabelsGridProps = {
  value: number | null
  onChange: (value: number) => void
}

const RibbonLabelsStartGrid: React.FC<RibbonLabelsGridProps> = ({ value, onChange }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const effectiveIndex = hoverIndex != null ? hoverIndex : value

  return (
    <div
      style={{
        display: "inline-block",
        padding: 8,
        border: "1px solid #d9d9d9",
        borderRadius: 4,
        background: "#fafafa",
      }}
    >
      <div
        style={{
          display: "grid",
          // Approximate an 8.5x11in portrait page: wider than tall,
          // with label cells that are much wider than they are tall.
          width: 200,
          aspectRatio: 8.5 / 11,
          gridTemplateColumns: "repeat(3, 2.625fr)",
          gridTemplateRows: "repeat(10, 1fr)",
          gap: 4,
        }}
      >
        {Array.from({ length: 30 }).map((_, i) => {
          const index = i + 1
          let background = "#ffffff"
          let borderColor = "#d9d9d9"

          if (effectiveIndex != null) {
            if (index < effectiveIndex) {
              background = "#f0f0f0" // already-used labels
            } else {
              background = "#e6f4ff" // will-print labels
            }
          }

          if (value === index) {
            borderColor = "#1677ff"
          } else if (hoverIndex === index) {
            borderColor = "#69b1ff"
          }

          return (
            <Tooltip
              key={index}
              title={`Start at label ${index}`}
            >
              <div
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
                onClick={() => onChange(index)}
                style={{
                  cursor: "pointer",
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                  background,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  userSelect: "none",
                }}
              >
                {index}
              </div>
            </Tooltip>
          )
        })}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 10,
          color: "#595959",
          textAlign: "center",
        }}
      >
        <span style={{ background: "#f0f0f0", padding: "0 4px", borderRadius: 2 }}>  </span>
        <span style={{ marginRight: 8, marginLeft: 4 }}> used</span>
        <span style={{ background: "#e6f4ff", padding: "0 4px", borderRadius: 2 }}>  </span>
        <span style={{ marginLeft: 4 }}> will print</span>
      </div>
    </div>
  )
}

function getAgeGroupSortKey(ageGroup: string | null | undefined): number {
  if (!ageGroup || typeof ageGroup !== "string") return Number.MAX_SAFE_INTEGER
  const match = ageGroup.match(/\d+/)
  if (!match) return Number.MAX_SAFE_INTEGER
  return parseInt(match[0], 10)
}

function ReportTile({ icon, title, description, onClick, primary, disabled, isNew }: ReportTileProps) {
  const descriptionInnerRef = useRef<HTMLDivElement | null>(null)
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0)

  useEffect(() => {
    if (descriptionInnerRef.current) {
      setDescriptionHeight(descriptionInnerRef.current.scrollHeight || 0)
    }
  }, [title, description])

  return (
    <Button
      type={primary ? "primary" : "default"}
      block
      style={{
        ...tileStyle,
        // Base min height for a compact closed tile; selected
        // tiles grow naturally as the description slides open.
        minHeight: 80,
      }}
      disabled={disabled}
      onClick={onClick}
    >
      <div
        style={{
          fontSize: 32,
          marginRight: 12,
          display: "flex",
          alignItems: "center",
          lineHeight: 0,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ textAlign: "left", flex: 1, overflow: "hidden" }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            marginBottom: 4,
            whiteSpace: "normal",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            wordBreak: "break-word",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          {isNew && (
            <span
              style={{
                marginLeft: 8,
                padding: "0 6px",
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                backgroundColor: "#52c41a",
                color: "white",
                lineHeight: "16px",
                flexShrink: 0,
              }}
            >
              NEW
            </span>
          )}
        </div>
        <div
          style={{
            maxHeight: primary ? descriptionHeight : 0,
            opacity: primary ? 1 : 0,
            overflow: "hidden",
            marginTop: primary ? 4 : 0,
            transition:
              "max-height 0.25s ease, opacity 0.2s ease, margin-top 0.2s ease",
          }}
        >
          <div
            ref={descriptionInnerRef}
            style={{
              fontSize: 13,
              opacity: 0.8,
              lineHeight: 1.4,
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {description}
          </div>
        </div>
      </div>
    </Button>
  )
}

interface ClubInfo {
  clubId?: number | string
  nameLong?: string
  club_name_long?: string
  club_name?: string
  name?: string
  entriesCount?: number
}

interface MeetInfo {
  unit_of_measure?: string
  meet_date?: string
  individualLimit?: number | null
  relayLimit?: number | null
  totalLimit?: number | null
}

interface MeetEvent {
  age_group?: string
}

interface UserInfo {
  role?: number | string
  club_id?: number | string
  lastAliasedClub?: number | null
}

type ReportBrandingOption = {
  id: number
  slotIndex: number
  slotLabel: string
  displayName: string
  imageWidth: number | null
  imageHeight: number | null
  dataUri: string
}

type ReportBrandingContext = {
  effectiveClub: ClubInfo | null
  approvedLogos: ReportBrandingOption[]
  defaultSlotIndex: number
}

type HeatSheetDownloadOptions = {
  brandingSlotIndex?: number
  logoDataUri?: string | null
  logoWidth?: number | null
  logoHeight?: number | null
}

const NO_LOGO_BRANDING_VALUE = -1

interface SwimMeetReportStudioProps {
  visible: boolean
  onClose: () => void
  onDownloadHeatSheetPdf?: (cols: number, options?: HeatSheetDownloadOptions) => void
  meetInfoArr?: MeetInfo | null
  meetEventsArr?: MeetEvent[] | null
  meetName?: string
  club?: ClubInfo | null
  meetId?: number | string
  user?: UserInfo | null
  context?: "heatSheet" | "entries" | "results"
  onOpenExportEntries?: (clubIds: Array<number | string>) => void
  onOpenExportResults?: () => void
  virtualMeetId?: number | null
  resultsPdfExists?: boolean
  labelsPdfExists?: boolean
  inline?: boolean
  onInlineCreateReportChange?: (config: { label: string; onClick: () => void }) => void
  initialSelectedReport?: ReportKey
  initialLineupViewMode?: "ageGroup" | "event" | "swimmer"
  keepInlinePreviewColumns?: boolean
}

const SwimMeetReportStudio: React.FC<SwimMeetReportStudioProps> = ({
  visible,
  onClose,
  onDownloadHeatSheetPdf,
  meetInfoArr,
  meetEventsArr,
  meetName,
  club,
  meetId,
  user,
  context = "heatSheet",
  onOpenExportEntries,
  onOpenExportResults,
  virtualMeetId = null,
  resultsPdfExists = false,
  labelsPdfExists = false,
  inline = false,
  onInlineCreateReportChange,
  initialSelectedReport,
  initialLineupViewMode = "ageGroup",
  keepInlinePreviewColumns = false,
}) => {
  const isActive = inline || visible

  let meetTitle = meetName || "Current Swim Meet"
  let courseLabel: string | null = null
  let dateLabel: string | null = null
  let dateForFilename: string | null = null

  if (meetInfoArr) {
    if (
      meetInfoArr.unit_of_measure &&
      (convertCourse as any)[meetInfoArr.unit_of_measure]
    ) {
      courseLabel = (convertCourse as any)[meetInfoArr.unit_of_measure]
    }
    if (meetInfoArr.meet_date) {
      dateLabel = moment.utc(meetInfoArr.meet_date).format("MMM D, YYYY")
      dateForFilename = moment
        .utc(meetInfoArr.meet_date)
        .format("YYYYMMDD")
    }
  }

  const [selectedReport, setSelectedReport] = useState<ReportKey>(
    initialSelectedReport
      ? initialSelectedReport
      : context === "entries"
        ? "lineup"
        : context === "results"
          ? "resultsExcel"
          : "heatSheet",
  )

  const singleReportMode = Boolean(inline && initialSelectedReport)
  const [heatSheetColumns, setHeatSheetColumns] = useState<number>(3)
  const [lineupColumns, setLineupColumns] = useState<number>(3)
  const [includeTimes, setIncludeTimes] = useState<boolean>(true)
  const [includeNotes, setIncludeNotes] = useState<boolean>(true)
  const [onlyRelays, setOnlyRelays] = useState<boolean>(false)
  const [lineupViewMode, setLineupViewMode] = useState<"ageGroup" | "event" | "swimmer">(
    initialLineupViewMode,
  )
  const [selectedClubId, setSelectedClubId] = useState<number | string | undefined>(
    club && club.clubId ? club.clubId : undefined,
  )
  const [selectedLineupClubIds, setSelectedLineupClubIds] = useState<
    Array<number | string>
  >([])
  const [meetClubs, setMeetClubs] = useState<any[]>([])
  const [summarySelectedClubIds, setSummarySelectedClubIds] = useState<
    Array<number | string>
  >([])
  const [summaryEventType, setSummaryEventType] = useState<
    "all" | "individual" | "relay"
  >("all")
  const [summaryColumns, setSummaryColumns] = useState<number>(2)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState<boolean>(false)
  const [previewDrawerDownloadLoading, setPreviewDrawerDownloadLoading] =
    useState<boolean>(false)
  const [lineupPreviewEmpty, setLineupPreviewEmpty] = useState<boolean>(false)
  const [heatSheetPreviewEmpty, setHeatSheetPreviewEmpty] = useState<boolean>(false)
  const [bestTimesPreviewRows, setBestTimesPreviewRows] = useState<any[] | null>(
    null,
  )
  const [resultsExcelPreview, setResultsExcelPreview] = useState<
    | {
      scores: any[]
      swimmerResults: any[]
      highPoints: any[]
    }
    | null
  >(null)
  const [exportSelectedClubIds, setExportSelectedClubIds] = useState<
    Array<number | string>
  >([])
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false)
  const [reportBrandingContext, setReportBrandingContext] =
    useState<ReportBrandingContext | null>(null)
  const [reportBrandingLoading, setReportBrandingLoading] =
    useState<boolean>(false)
  const [resultsPdfBrandingSlotIndex, setResultsPdfBrandingSlotIndex] =
    useState<number>(0)
  const [lineupBrandingSlotIndex, setLineupBrandingSlotIndex] =
    useState<number>(0)
  const [heatSheetBrandingSlotIndex, setHeatSheetBrandingSlotIndex] =
    useState<number>(0)
  const [entrySummaryBrandingSlotIndex, setEntrySummaryBrandingSlotIndex] =
    useState<number>(0)
  const [swimmerSummaryBrandingSlotIndex, setSwimmerSummaryBrandingSlotIndex] =
    useState<number>(0)
  const [swimmerSummaryColumns, setSwimmerSummaryColumns] = useState<number>(2)
  const [swimmerSummaryGenderGrouping, setSwimmerSummaryGenderGrouping] = useState<"combined" | "separate">("combined")
  const [swimmerSummarySelectedClubIds, setSwimmerSummarySelectedClubIds] = useState<
    Array<number | string>
  >([])
  const activeBrandingClubId = Number(
    club?.clubId || (club as any)?.club_id || user?.lastAliasedClub || 0,
  )
  const selectedClubIdKey =
    selectedClubId !== undefined && selectedClubId !== null
      ? String(selectedClubId)
      : null
  const clubPropIdKey =
    club?.clubId !== undefined && club?.clubId !== null
      ? String(club.clubId)
      : (club as any)?.club_id !== undefined && (club as any)?.club_id !== null
        ? String((club as any).club_id)
        : null

  const clubDisplayName =
    (club &&
      (club.nameLong || club.club_name_long || club.club_name || club.name)) ||
    "Current Club"
  const selectedMeetClub = useMemo(
    () =>
      selectedClubIdKey
        ? meetClubs.find((c) => String(c.clubId) === selectedClubIdKey) || null
        : null,
    [meetClubs, selectedClubIdKey],
  )
  const selectedClubEntriesCount = useMemo(() => {
    if (
      clubPropIdKey &&
      selectedClubIdKey &&
      clubPropIdKey === selectedClubIdKey &&
      club?.entriesCount !== undefined &&
      club?.entriesCount !== null
    ) {
      const parsed = Number(club.entriesCount)
      return Number.isNaN(parsed) ? null : parsed
    }

    if (
      selectedMeetClub &&
      selectedMeetClub.entriesCount !== undefined &&
      selectedMeetClub.entriesCount !== null
    ) {
      const parsed = Number(selectedMeetClub.entriesCount)
      return Number.isNaN(parsed) ? null : parsed
    }

    return null
  }, [club, clubPropIdKey, selectedClubIdKey, selectedMeetClub])

  const previewableReports: ReportKey[] = useMemo(
    () => [
      "resultsExcel",
      "resultsPdf",
      "ribbonLabelsPdf",
      "heatSheet",
      "lineup",
      "entrySummary",
      "swimmerSummary",
      "bestTimes",
    ],
    [],
  )

  const isPreviewableSelection = previewableReports.includes(selectedReport)

  const reportBrandingOptions = useMemo(
    () => reportBrandingContext?.approvedLogos || [],
    [reportBrandingContext?.approvedLogos],
  )
  const reportBrandingClubName =
    reportBrandingContext?.effectiveClub?.nameLong || clubDisplayName

  const findBrandingOptionBySlot = (slotIndex: number) =>
    slotIndex === NO_LOGO_BRANDING_VALUE
      ? null
      : reportBrandingOptions.find((logo) => logo.slotIndex === slotIndex) || null

  const selectedResultsPdfBrandingOption = useMemo(
    () => findBrandingOptionBySlot(resultsPdfBrandingSlotIndex),
    [resultsPdfBrandingSlotIndex, reportBrandingOptions],
  )
  const selectedLineupBrandingOption = useMemo(
    () => findBrandingOptionBySlot(lineupBrandingSlotIndex),
    [lineupBrandingSlotIndex, reportBrandingOptions],
  )
  const selectedHeatSheetBrandingOption = useMemo(
    () => findBrandingOptionBySlot(heatSheetBrandingSlotIndex),
    [heatSheetBrandingSlotIndex, reportBrandingOptions],
  )
  const selectedEntrySummaryBrandingOption = useMemo(
    () => findBrandingOptionBySlot(entrySummaryBrandingSlotIndex),
    [entrySummaryBrandingSlotIndex, reportBrandingOptions],
  )
  const selectedSwimmerSummaryBrandingOption = useMemo(
    () => findBrandingOptionBySlot(swimmerSummaryBrandingSlotIndex),
    [swimmerSummaryBrandingSlotIndex, reportBrandingOptions],
  )

  // Results PDF options
  const [resultsPdfClubIds, setResultsPdfClubIds] = useState<
    Array<number | string>
  >([])
  const [resultsPdfEventIds, setResultsPdfEventIds] = useState<
    Array<number | string>
  >([])
  const [resultsPdfMode, setResultsPdfMode] = useState<"event" | "heat">(
    "event",
  )
  const [resultsPdfColumns, setResultsPdfColumns] = useState<number>(1)
  const [resultsPdfAllPlaces, setResultsPdfAllPlaces] = useState<boolean>(true)
  const [resultsPdfMinPlace, setResultsPdfMinPlace] = useState<string>("")
  const [resultsPdfMaxPlace, setResultsPdfMaxPlace] = useState<string>("")
  const [resultsPdfPlacesError, setResultsPdfPlacesError] = useState<string | null>(
    null,
  )
  const [resultsPdfFirstHeatOnly, setResultsPdfFirstHeatOnly] =
    useState<boolean>(false)
  const [resultsPdfLastHeatOnly, setResultsPdfLastHeatOnly] =
    useState<boolean>(false)
  const [resultsPdfShowTeamScores, setResultsPdfShowTeamScores] =
    useState<boolean>(true)
  const [resultsPdfShowSwimPoints, setResultsPdfShowSwimPoints] =
    useState<boolean>(true)
  const [resultsPdfShowDqs, setResultsPdfShowDqs] = useState<boolean>(true)
  const [resultsPdfShowDqTimes, setResultsPdfShowDqTimes] =
    useState<boolean>(false)
  const [resultsPdfShowDqReasons, setResultsPdfShowDqReasons] =
    useState<boolean>(true)
  const [resultsPdfShowExhibition, setResultsPdfShowExhibition] =
    useState<boolean>(true)

  // Ribbon Labels (Avery 5160) options
  const [ribbonLabelsStartLabel, setRibbonLabelsStartLabel] =
    useState<string>("1")
  const [ribbonLabelsStartLabelError, setRibbonLabelsStartLabelError] =
    useState<string | null>(null)
  const [ribbonLabelsSortMode, setRibbonLabelsSortMode] = useState<
    "event" | "swimmer" | "ageGroup"
  >("event")
  const [ribbonLabelsIncludeExhibition, setRibbonLabelsIncludeExhibition] =
    useState<boolean>(false)
  const [ribbonLabelsDqMode, setRibbonLabelsDqMode] =
    useState<boolean>(false)
  const [ribbonLabelsShowDqTimes, setRibbonLabelsShowDqTimes] =
    useState<boolean>(false)
  const [ribbonLabelsShowDqReasons, setRibbonLabelsShowDqReasons] =
    useState<boolean>(true)
  const [ribbonLabelsShowPoints, setRibbonLabelsShowPoints] =
    useState<boolean>(true)
  const [ribbonLabelsAllPlaces, setRibbonLabelsAllPlaces] =
    useState<boolean>(true)
  const [ribbonLabelsMinPlace, setRibbonLabelsMinPlace] =
    useState<number | null>(null)
  const [ribbonLabelsMaxPlace, setRibbonLabelsMaxPlace] =
    useState<number | null>(null)
  const [ribbonLabelsPlacesError, setRibbonLabelsPlacesError] =
    useState<string | null>(null)
  const [ribbonLabelsPlaceMode, setRibbonLabelsPlaceMode] = useState<
    "event" | "heat"
  >("event")
  const [ribbonLabelsPlaceCapEnabled, setRibbonLabelsPlaceCapEnabled] =
    useState<boolean>(false)
  const [ribbonLabelsPlaceCap, setRibbonLabelsPlaceCap] = useState<
    number | null
  >(null)
  const [ribbonLabelsEventIds, setRibbonLabelsEventIds] = useState<
    Array<number | string>
  >([])
  const [ribbonLabelsClubIds, setRibbonLabelsClubIds] = useState<
    Array<number | string>
  >([])
  const [ribbonLabelsSwimmerOptions, setRibbonLabelsSwimmerOptions] = useState<
    Array<{
      swimmerId: number
      clubId: number
      clubCode: string
      firstName: string
      lastName: string
      gender: string
      ageGroupLabel: string
    }>
  >([])
  const [ribbonLabelsSwimmerIds, setRibbonLabelsSwimmerIds] = useState<
    Array<number>
  >([])

  // Meet events export modal state
  const [exportEventsModalVisible, setExportEventsModalVisible] = useState<boolean>(false)
  const [exportEventsCsvLoading, setExportEventsCsvLoading] = useState<boolean>(false)

  // HYV export modal state
  const [hyvModalVisible, setHyvModalVisible] = useState<boolean>(false)
  const [hyvAgeUpDate, setHyvAgeUpDate] = useState<moment.Moment | null>(null)
  const [hyvEndDate, setHyvEndDate] = useState<moment.Moment | null>(null)
  const [hyvFacilityName, setHyvFacilityName] = useState<string>("")
  const [hyvConversionCode, setHyvConversionCode] = useState<"CN" | "CY">("CN")
  const [hyvFormat, setHyvFormat] = useState<"ev3" | "hyv" | "both">("ev3")

  // Entry limits override for EV3 export
  const [ev3LimitsEnabled, setEv3LimitsEnabled] = useState(false)
  const [ev3IndLimit, setEv3IndLimit] = useState<number | null>(null)
  const [ev3RelayLimit, setEv3RelayLimit] = useState<number | null>(null)
  const [ev3TotalLimit, setEv3TotalLimit] = useState<number | null>(null)

  const roleName = useMemo(() => {
    if (!user || user.role === undefined) return null
    return (roleMapping as any)[user.role] || null
  }, [user])

  const isLeagueAdmin = roleName === "League Admin"

  const isEntriesLeagueByEvent = useMemo(
    () =>
      context === "entries" &&
      selectedReport === "lineup" &&
      (lineupViewMode === "event" || lineupViewMode === "swimmer") &&
      isLeagueAdmin,
    [context, selectedReport, lineupViewMode, isLeagueAdmin],
  )

  const isOpenOnlyMeet = useMemo(() => {
    if (!Array.isArray(meetEventsArr) || meetEventsArr.length === 0) return false
    return meetEventsArr.every((ev) => {
      const ag =
        ev && typeof ev.age_group === "string"
          ? ev.age_group.trim().toLowerCase()
          : ""
      return ag === "open"
    })
  }, [meetEventsArr])

  const ageGroupOptions = useMemo(
    () => {
      if (!Array.isArray(meetEventsArr)) return [] as { label: string; value: string }[]
      const seen: Record<string, boolean> = {}
      const out: { label: string; value: string }[] = []
      meetEventsArr.forEach((ev) => {
        const ageGroup = ev.age_group
        if (!ageGroup) return
        if (!seen[ageGroup]) {
          seen[ageGroup] = true
          out.push({ label: ageGroup, value: ageGroup })
        }
      })
      out.sort((a, b) => {
        const aKey = getAgeGroupSortKey(a.value)
        const bKey = getAgeGroupSortKey(b.value)
        if (aKey !== bKey) return aKey - bKey
        return String(a.value).localeCompare(String(b.value))
      })
      return out
    },
    [meetEventsArr],
  )

  const [selectedAgeGroups, setSelectedAgeGroups] = useState<string[]>([])
  const [genderFilter, setGenderFilter] = useState<"both" | "girls" | "boys">("both")

  useEffect(() => {
    if (ageGroupOptions.length > 0 && selectedAgeGroups.length === 0) {
      setSelectedAgeGroups(ageGroupOptions.map((opt) => opt.value))
    }
  }, [ageGroupOptions, selectedAgeGroups.length])

  useEffect(() => {
    if (isOpenOnlyMeet) {
      if (selectedAgeGroups.length !== 1 || selectedAgeGroups[0] !== "Open") {
        setSelectedAgeGroups(["Open"])
      }
    }
  }, [isOpenOnlyMeet, selectedAgeGroups])

  useEffect(() => {
    if (!isActive) return

    if (singleReportMode && initialSelectedReport) {
      setSelectedReport(initialSelectedReport)
    } else {
      setSelectedReport(
        context === "entries"
          ? "lineup"
          : context === "results"
            ? "resultsExcel"
            : "heatSheet",
      )
    }

    setPreviewUrl(null)
    setPreviewLoading(false)
    setLineupPreviewEmpty(false)
    setHeatSheetPreviewEmpty(false)
    setLineupViewMode(initialLineupViewMode)
  }, [isActive, context, initialLineupViewMode, initialSelectedReport, singleReportMode])

  useEffect(() => {
    if (!isActive) return

    let cancelled = false

    const run = async () => {
      setReportBrandingLoading(true)
      try {
        const response: any = await swimminglyApi.get(
          `/api/team-logos/report-branding-context?clubId=${activeBrandingClubId || ""}`,
        )

        if (cancelled) return
        if (response?.status === "error") {
          setReportBrandingContext(null)
          return
        }

        setReportBrandingContext({
          effectiveClub: response?.effectiveClub || null,
          approvedLogos: Array.isArray(response?.approvedLogos)
            ? response.approvedLogos
            : [],
          defaultSlotIndex: Number(response?.defaultSlotIndex || 0),
        })
      } catch (_err) {
        if (!cancelled) {
          setReportBrandingContext(null)
        }
      } finally {
        if (!cancelled) {
          setReportBrandingLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [activeBrandingClubId, isActive])

  useEffect(() => {
    if (!reportBrandingContext) return

    const availableSlotIndexes = [
      NO_LOGO_BRANDING_VALUE,
      ...reportBrandingOptions.map((logo) => logo.slotIndex),
    ]
    const defaultSlotIndex = availableSlotIndexes.includes(
      reportBrandingContext.defaultSlotIndex,
    )
      ? reportBrandingContext.defaultSlotIndex
      : availableSlotIndexes[1] ?? availableSlotIndexes[0] ?? 0

    if (!availableSlotIndexes.includes(resultsPdfBrandingSlotIndex)) {
      setResultsPdfBrandingSlotIndex(defaultSlotIndex)
    }
    if (!availableSlotIndexes.includes(lineupBrandingSlotIndex)) {
      setLineupBrandingSlotIndex(defaultSlotIndex)
    }
    if (!availableSlotIndexes.includes(heatSheetBrandingSlotIndex)) {
      setHeatSheetBrandingSlotIndex(defaultSlotIndex)
    }
    if (!availableSlotIndexes.includes(entrySummaryBrandingSlotIndex)) {
      setEntrySummaryBrandingSlotIndex(defaultSlotIndex)
    }
    if (!availableSlotIndexes.includes(swimmerSummaryBrandingSlotIndex)) {
      setSwimmerSummaryBrandingSlotIndex(defaultSlotIndex)
    }
  }, [
    entrySummaryBrandingSlotIndex,
    heatSheetBrandingSlotIndex,
    lineupBrandingSlotIndex,
    reportBrandingContext,
    reportBrandingOptions,
    resultsPdfBrandingSlotIndex,
    swimmerSummaryBrandingSlotIndex,
  ])

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth < 768)
      }
    }
    updateIsMobile()
    window.addEventListener("resize", updateIsMobile)
    return () => window.removeEventListener("resize", updateIsMobile)
  }, [])

  function createBestTimesCSV(meetData: any[]): string {
    let theAgeGroups = meetData.map(
      (x) => `${x.eventGender}|${x.eventAgeGroup}`,
    )
    let distinctAgeGroups = theAgeGroups.filter((v, i, a) => a.indexOf(v) === i)
    distinctAgeGroups = distinctAgeGroups.map((x) => {
      const ageGroupInfo: any = {
        eventGender: x.split("|")[0],
        eventAgeGroup: x.split("|")[1],
      }
      const allRecords = meetData.filter(
        (y) =>
          y.eventGender === ageGroupInfo.eventGender &&
          y.eventAgeGroup === ageGroupInfo.eventAgeGroup,
      )
      const events = allRecords.map(
        (y) => `${y.eventDistance}|${y.eventDescription}`,
      )
      const distinctEvents = events.filter((v, i, a) => a.indexOf(v) === i)
      ageGroupInfo.eventCount = distinctEvents.length
      return ageGroupInfo
    })

    const maxEvents = Math.max(...distinctAgeGroups.map((x: any) => x.eventCount))

    const numColumns = 3 + maxEvents
    const poolCourse = meetData[0]?.poolLength || ""
    const meetNameLocal = meetData[0]?.meetName || ""
    const meetDateLocal = meetData[0]?.meet_date || ""

    let out =
      "Best Times by event (NOTE - only individual events shown below)" +
      ",".repeat(numColumns - 1) +
      "\r\n"
    out +=
      "This meet is set up in a " +
      poolCourse +
      " pool. All times below are standardized to that length." +
      " Converted times are denoted with a *." +
      ",".repeat(numColumns - 1) +
      "\r\n"
    out += meetNameLocal + ",".repeat(numColumns - 1) + "\r\n"
    out += meetDateLocal + ",".repeat(numColumns - 1) + "\r\n\r\n"

    for (let i = 0; i < distinctAgeGroups.length; i += 1) {
      const theAgeGroup: any = distinctAgeGroups[i]
      let remainingCommas = numColumns - 1
      out +=
        `${theAgeGroup.eventAgeGroup} ${theAgeGroup.eventGender}, League Age, Swimmer Number`
      remainingCommas -= 2
      const ageGroupEventNames = meetData
        .filter(
          (x) =>
            x.eventAgeGroup === theAgeGroup.eventAgeGroup &&
            x.eventGender === theAgeGroup.eventGender,
        )
        .map((x) => `${x.eventDistance} ${x.eventDescription}`)
        .filter((v, i, a) => a.indexOf(v) === i)

      const ageGroupEvents = ageGroupEventNames.map((x) => {
        const forOutput = meetData.find(
          (y) => `${y.eventDistance} ${y.eventDescription}` === x,
        )
        return {
          name: `${forOutput.eventDistance} ${forOutput.eventDescription}`,
          distance: forOutput.eventDistance,
          stroke: forOutput.eventDescription,
        }
      })

      for (let j = 0; j < ageGroupEvents.length; j += 1) {
        const thisEvent = ageGroupEvents[j]
        out += `,${thisEvent.name}`
        remainingCommas -= 1
      }
      out += ",".repeat(remainingCommas) + "\r\n"

      const results = meetData.filter(
        (x) =>
          x.eventAgeGroup === theAgeGroup.eventAgeGroup &&
          x.eventGender === theAgeGroup.eventGender,
      )
      const swimmerNumbers = results
        .map((x) => x.swimmer_number)
        .filter((v, i, a) => a.indexOf(v) === i)

      for (let j = 0; j < swimmerNumbers.length; j += 1) {
        const swimmerResults = results.filter(
          (x) => x.swimmer_number === swimmerNumbers[j],
        )
        if (!swimmerResults.length) continue
        const swimmerName = `${swimmerResults[0].firstName} ${swimmerResults[0].lastName}`
        out += `${swimmerName},${swimmerResults[0].leagueAge},${swimmerNumbers[
          j
        ].toString()}`

        for (let k = 0; k < ageGroupEvents.length; k += 1) {
          const thisEvent = ageGroupEvents[k]
          const swimmerBestTime = swimmerResults
            .filter(
              (x) =>
                x.eventDistance === thisEvent.distance &&
                x.eventDescription === thisEvent.stroke,
            )
            .map((x) => x.bestTime)[0]
          out += `,="${swimmerBestTime}"`
        }
        out += "\r\n"
      }
      out += ",".repeat(numColumns - 1) + "\r\n"
    }
    return out
  }

  useEffect(() => {
    const fetchClubs = async () => {
      if (!meetId || String(meetId) === "-1") return
      try {
        const res = await (swimminglyApi as any)
          .get(gen(`/api/getMeetClubs/${meetId}`))
        if (res && res.status === "success" && Array.isArray(res.meetClubs)) {
          setMeetClubs(res.meetClubs)
        }
      } catch (e) {
        // non-fatal; lineup can still run with current club only
        // eslint-disable-next-line no-console
        console.error("Error fetching meet clubs", e)
      }
    }
    fetchClubs()
  }, [meetId])

  useEffect(() => {
    if (!meetClubs || meetClubs.length === 0) return

    if (summarySelectedClubIds.length === 0) {
      setSummarySelectedClubIds(meetClubs.map((c) => c.clubId))
    }

    if (swimmerSummarySelectedClubIds.length === 0) {
      setSwimmerSummarySelectedClubIds(meetClubs.map((c) => c.clubId))
    }

    if (exportSelectedClubIds.length === 0) {
      const roleNameLocal = roleName
      const canSelectAllClubs =
        roleNameLocal === "League Admin" ||
        roleNameLocal === "Hydro Admin" ||
        (user && user.role === 2)

      if (canSelectAllClubs) {
        // League / Hydro / Swimmingly admins: default to all clubs
        setExportSelectedClubIds(meetClubs.map((c) => c.clubId))
      } else {
        // Club admins and others: default to their own club only.
        // Prefer the active club prop (impersonateClub) over user.club_id so that
        // admins or users whose home club differs from the aliased club don't
        // accidentally land on the first team in the dropdown.
        const activeClubId =
          club?.clubId ??
          (club as any)?.club_id ??
          (user && user.club_id)
        const userClub = activeClubId
          ? meetClubs.find((c) => String(c.clubId) === String(activeClubId))
          : null

        if (userClub) {
          setExportSelectedClubIds([userClub.clubId])
        } else {
          // Fallback: first club in list
          setExportSelectedClubIds([meetClubs[0].clubId])
        }
      }
    }
  }, [
    meetClubs,
    summarySelectedClubIds.length,
    swimmerSummarySelectedClubIds.length,
    exportSelectedClubIds.length,
    roleName,
    user,
    club,
  ])

  // Default Results PDF clubs to all meet clubs
  useEffect(() => {
    if (!meetClubs || meetClubs.length === 0) return
    if (resultsPdfClubIds.length > 0) return
    setResultsPdfClubIds(meetClubs.map((c) => c.clubId))
  }, [meetClubs, resultsPdfClubIds.length])

  // Default Ribbon Labels clubs to all meet clubs
  useEffect(() => {
    if (!meetClubs || meetClubs.length === 0) return
    if (ribbonLabelsClubIds.length > 0) return
    setRibbonLabelsClubIds(meetClubs.map((c) => c.clubId))
  }, [meetClubs, ribbonLabelsClubIds.length])

  // Load Ribbon Labels swimmer options whenever meet/clubs change.
  useEffect(() => {
    if (!meetId) return
    if (!ribbonLabelsClubIds || ribbonLabelsClubIds.length === 0) return

    let cancelled = false

    const run = async () => {
      try {
        const body: any = {
          meetId,
          clubIds: ribbonLabelsClubIds,
          selectedAgeGroups,
          genderFilter,
        }

        const response = await fetch("/api/getRibbonLabelsSwimmers", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          throw new Error(
            `Failed to load swimmers for ribbon labels: ${response.status}`,
          )
        }

        const data = await response.json()
        if (cancelled) return

        const swimmers = Array.isArray(data?.swimmers) ? data.swimmers : []
        setRibbonLabelsSwimmerOptions(swimmers)

        const allIds = swimmers
          .map((s: any) =>
            s && s.swimmerId != null ? Number(s.swimmerId) : null,
          )
          .filter((id: any) => typeof id === "number" && !Number.isNaN(id))

        setRibbonLabelsSwimmerIds(allIds as number[])
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error loading swimmers for ribbon labels", err)
        if (!cancelled) {
          setRibbonLabelsSwimmerOptions([])
          setRibbonLabelsSwimmerIds([])
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [meetId, ribbonLabelsClubIds, selectedAgeGroups, genderFilter])

  // Default Results PDF events to all meet events
  useEffect(() => {
    if (!Array.isArray(meetEventsArr) || meetEventsArr.length === 0) return
    if (resultsPdfEventIds.length > 0) return

    const allEventIds: Array<number | string> = []
      ; (meetEventsArr as any[]).forEach((ev) => {
        const id =
          ev.id != null
            ? ev.id
            : ev.swimEventId != null
              ? ev.swimEventId
              : null
        if (id != null) {
          allEventIds.push(id)
        }
      })

    if (allEventIds.length > 0) {
      setResultsPdfEventIds(allEventIds)
    }
  }, [meetEventsArr, resultsPdfEventIds.length])

  // Default Ribbon Labels events to all meet events on initial load.
  // Once the user changes the selection (including "Deselect all"),
  // we stop auto-populating so their choice is respected.
  const hasInitialRibbonEvents = useRef(false)

  useEffect(() => {
    if (!Array.isArray(meetEventsArr) || meetEventsArr.length === 0) return
    if (hasInitialRibbonEvents.current) return

    const allEventIds: Array<number | string> = []
      ; (meetEventsArr as any[]).forEach((ev) => {
        const id =
          ev.id != null
            ? ev.id
            : ev.swimEventId != null
              ? ev.swimEventId
              : null
        if (id != null) {
          allEventIds.push(id)
        }
      })

    if (allEventIds.length > 0) {
      setRibbonLabelsEventIds(allEventIds)
      hasInitialRibbonEvents.current = true
    }
  }, [meetEventsArr])

  useEffect(() => {
    if (!meetClubs || meetClubs.length === 0) return

    // If nothing selected yet, prefer the active club prop (impersonateClub) over
    // user.club_id so admins whose home club isn't in the meet don't land on
    // the first team in the dropdown.
    if (!selectedClubId) {
      const activeClubId =
        club?.clubId ??
        (club as any)?.club_id ??
        (user && user.club_id)
      const userClub = activeClubId
        ? meetClubs.find((c) => String(c.clubId) === String(activeClubId))
        : null
      if (userClub) {
        setSelectedClubId(userClub.clubId)
      } else {
        setSelectedClubId(meetClubs[0].clubId)
      }
    }
  }, [meetClubs, selectedClubId, user, club])

  const handleGenerateHeatSheet = async () => {
    const cols = heatSheetColumns === 2 ? 2 : 3
    if (onDownloadHeatSheetPdf) {
      try {
        onDownloadHeatSheetPdf(cols, {
          brandingSlotIndex: heatSheetBrandingSlotIndex,
          logoDataUri: selectedHeatSheetBrandingOption?.dataUri || null,
          logoWidth: selectedHeatSheetBrandingOption?.imageWidth || null,
          logoHeight: selectedHeatSheetBrandingOption?.imageHeight || null,
        })
        return
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error generating heat sheet PDF", err)
        return
      }
    }

    if (!meetId || !club || !club.clubId) {
      message.error("Missing meet or club information for heat sheet download.")
      return
    }

    try {
      const response = await swimminglyApi.get(
        gen(`/api/getHeatSheetJson/meet=${meetId}/club=${club.clubId}`),
      )

      if (!response?.heatSheetData || !Array.isArray(response.heatSheetData.events)) {
        message.error("Unable to build the heat sheet PDF for this meet.")
        return
      }

      generateHeatSheetPDF(response.heatSheetData, {}, cols, {
        brandingSlotIndex: heatSheetBrandingSlotIndex,
        logoDataUri: selectedHeatSheetBrandingOption?.dataUri || null,
        logoWidth: selectedHeatSheetBrandingOption?.imageWidth || null,
        logoHeight: selectedHeatSheetBrandingOption?.imageHeight || null,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error generating fallback heat sheet PDF", err)
      message.error("Problem generating heat sheet PDF. Please try again.")
    }
  }

  const handleConfirmHyvExport = async () => {
    if (!meetId) {
      message.error("Missing meet information for export.")
      return
    }

    if (!hyvAgeUpDate) {
      message.error("Please select an age-up date.")
      return
    }

    if (!hyvEndDate) {
      message.error("Please select a meet end date.")
      return
    }

    const ageUpDateStr = hyvAgeUpDate.format("MM/DD/YYYY")
    const endDateStr = hyvEndDate.format("MM/DD/YYYY")

    const params = new URLSearchParams()
    params.set("ageUpDate", ageUpDateStr)
    params.set("endDate", endDateStr)
    if (hyvFacilityName && hyvFacilityName.trim()) {
      params.set("facilityName", hyvFacilityName.trim())
    }
    const courseLabelUpper = (courseLabel || "").toUpperCase()
    const isLCM = courseLabelUpper.includes("LCM") || courseLabelUpper === "L"
    if (isLCM && hyvConversionCode) {
      params.set("courseMeetCode", hyvConversionCode)
    }
    params.set("format", hyvFormat)

    // Append entry limit overrides when the user has enabled them (EV3/both only)
    const includesEv3 = hyvFormat === "ev3" || hyvFormat === "both"
    if (includesEv3 && ev3LimitsEnabled) {
      params.set("ev3IndividualLimit", String(ev3IndLimit ?? 0))
      params.set("ev3RelayLimit", String(ev3RelayLimit ?? 0))
      params.set("ev3TotalLimit", String(ev3TotalLimit ?? 0))
    }

    try {
      const file = await (swimminglyApi as any).get(
        gen(`/api/exportHytekMeetEvents/${meetId}?${params.toString()}`),
      )
      const safeMeetTitle = meetTitle || "MeetEvents"
      const dateStamp = meetInfoArr?.meet_date
        ? (() => {
          const d = new Date(meetInfoArr.meet_date)
          if (isNaN(d.getTime())) return ""
          const day = String(d.getUTCDate()).padStart(2, "0")
          const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" }).replace(".", "")
          return `${day}${month}${d.getUTCFullYear()}`
        })()
        : ""
      const safeName = safeMeetTitle.replace(/[^A-Za-z0-9_-]+/g, "_")
      download(file, `Meet Events-${safeName}${dateStamp ? `-${dateStamp}` : ""}-001.zip`)
      setHyvModalVisible(false)
    } catch (err) {
      message.error("There was a problem exporting the Meet Events file.")
    }
  }

  const handleGenerateBestTimesMatrix = async () => {
    if (!meetId || !selectedClubId) {
      message.error("Missing meet or club information for Best Time Matrix.")
      return
    }

    try {
      const data = await (swimminglyApi as any)
        .post(gen("/api/bestTimesMatrixPost"))
        .body({
          meetId,
          clubId: parseInt(String(selectedClubId), 10),
        })

      if (!data || !Array.isArray(data.results) || data.results.length === 0) {
        message.error("Unable to build Best Time Matrix for this meet.")
        return
      }

      const csv = createBestTimesCSV(data.results)

      const safeMeetName = (meetNameLocal: string | undefined | null) =>
        (meetNameLocal || "Meet")
          .replace(/[^A-Za-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "")

      const safeClubName = (clubNameLocal: string | undefined | null) =>
        (clubNameLocal || clubDisplayName || "Club")
          .replace(/[^A-Za-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "")

      const meetNameForFile = safeMeetName(meetName)
      const clubNameForFile = safeClubName(clubDisplayName)

      saveFile(
        csv,
        `BestTimesMatrix_${meetNameForFile || "Meet"}_${clubNameForFile || "Club"
        }.csv`,
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error generating Best Time Matrix", err)
      message.error("Something went wrong generating the Best Time Matrix.")
    }
  }

  const handleGenerateEntrySummary = async () => {
    if (!meetId) {
      message.error("Missing meet information for entry summary report.")
      return
    }
    if (!summarySelectedClubIds || summarySelectedClubIds.length === 0) {
      message.error("Select at least one club to include.")
      return
    }

    try {
      const data = await (swimminglyApi as any)
        .post(gen("/api/meetEntrySummaryPost"))
        .body({
          meetId,
          clubIds: summarySelectedClubIds,
          eventType: summaryEventType,
        })

      if (!data || !data.eventsSummary) {
        message.error("Unable to build meet entry summary for this meet.")
        return
      }

      generateMeetEntrySummaryPDF(
        {
          eventsSummary: data.eventsSummary,
          meetName: data.meetName,
          meetDate: data.meetDate,
          meetTotalsAllClubs: data.meetTotalsAllClubs,
        },
        {
          columns: summaryColumns,
          logoDataUri: selectedEntrySummaryBrandingOption?.dataUri || null,
          logoWidth: selectedEntrySummaryBrandingOption?.imageWidth || null,
          logoHeight: selectedEntrySummaryBrandingOption?.imageHeight || null,
        },
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error generating meet entry summary", err)
      message.error("Something went wrong generating the meet entry summary.")
    }
  }

  const handleGenerateSwimmerSummary = async () => {
    const data = await loadSwimmerSummaryData()
    if (!data) return

    generateMeetSwimmerSummaryPDF(
      {
        clubSummary: data.clubSummary,
        ageGroupBreakdown: data.ageGroupBreakdown,
        meetName: data.meetName,
        meetDate: data.meetDate,
        meetTotalsAllClubs: data.meetTotalsAllClubs,
      },
      {
        columns: swimmerSummaryColumns,
        logoDataUri: selectedSwimmerSummaryBrandingOption?.dataUri || null,
        logoWidth: selectedSwimmerSummaryBrandingOption?.imageWidth || null,
        logoHeight: selectedSwimmerSummaryBrandingOption?.imageHeight || null,
      },
    )
  }

  const buildSwimmerSummaryCsv = (data: {
    clubSummary: Array<{
      clubId: string | number
      clubName: string
      totalSwimmers: number
      relayOnlySwimmers: number
      exhibitionOnlySwimmers: number
    }>
    ageGroupBreakdown: Array<{
      ageGroup: string
      clubs: Array<{
        clubId: string | number
        clubName: string
        totalSwimmers: number
        relayOnlySwimmers: number
        exhibitionOnlySwimmers: number
      }>
    }>
    meetName?: string | null
    meetDate?: string | null
    meetTotalsAllClubs?: {
      totalSwimmers: number
      relayOnlySwimmers: number
      exhibitionOnlySwimmers: number
    } | null
  }): string => {
    const csvValue = (value: unknown): string => {
      const text = value === null || value === undefined ? "" : String(value)
      return `"${text.replace(/"/g, '""')}"`
    }

    const rows: Array<Array<unknown>> = [
      ["Meet Summary by Swimmer"],
      [data.meetName || ""],
      [data.meetDate || ""],
      [""],
    ]

    const totals = data.meetTotalsAllClubs || {
      totalSwimmers: 0,
      relayOnlySwimmers: 0,
      exhibitionOnlySwimmers: 0,
    }

    rows.push([
      "Meet Totals",
      "Swimmers",
      totals.totalSwimmers,
      "Relay Only",
      totals.relayOnlySwimmers,
      "Exhibition Only",
      totals.exhibitionOnlySwimmers,
    ])
    rows.push([""])

    rows.push([
      "Club Summary",
      "Club",
      "Total Swimmers",
      "Relay Only Swimmers",
      "Exhibition Only Swimmers",
    ])
    data.clubSummary.forEach((club) => {
      rows.push([
        "",
        club.clubName,
        club.totalSwimmers,
        club.relayOnlySwimmers,
        club.exhibitionOnlySwimmers,
      ])
    })

    rows.push([""])

    data.ageGroupBreakdown.forEach((ageGroupRow) => {
      rows.push([ageGroupRow.ageGroup])
      rows.push([
        "",
        "Club",
        "Total Swimmers",
        "Relay Only Swimmers",
        "Exhibition Only Swimmers",
      ])
      ageGroupRow.clubs.forEach((club) => {
        rows.push([
          "",
          club.clubName,
          club.totalSwimmers,
          club.relayOnlySwimmers,
          club.exhibitionOnlySwimmers,
        ])
      })
      rows.push([""])
    })

    return `${rows.map((row) => row.map(csvValue).join(",")).join("\n")}\n`
  }

  const loadSwimmerSummaryData = async () => {
    if (!meetId) {
      message.error("Missing meet information for swimmer summary report.")
      return null
    }
    if (!swimmerSummarySelectedClubIds || swimmerSummarySelectedClubIds.length === 0) {
      message.error("Select at least one club to include.")
      return null
    }

    try {
      const data = await (swimminglyApi as any)
        .post(gen("/api/meetSwimmerSummaryPost"))
        .body({
          meetId,
          clubIds: swimmerSummarySelectedClubIds,
          selectedAgeGroups,
          genderFilter,
          genderGrouping: swimmerSummaryGenderGrouping,
        })

      if (!data || !data.clubSummary) {
        message.error("Unable to build meet swimmer summary for this meet.")
        return null
      }

      return data
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error generating meet swimmer summary", err)
      message.error("Something went wrong generating the meet swimmer summary.")
      return null
    }

  }

  const handleDownloadSwimmerSummaryCsv = async () => {
    const data = await loadSwimmerSummaryData()
    if (!data) return

    const csv = buildSwimmerSummaryCsv(data)
    const safeMeetName = (data.meetName || meetTitle || "Meet")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
    saveFile(csv, `${safeMeetName || "Meet"}__SwimmerSummary.csv`)
  }

  const handleDownloadResultsExcel = async () => {
    if (!meetId) {
      message.error("Missing meet information for results export.")
      return
    }

    try {
      const file = await (swimminglyApi as any)
        .post(gen("/api/createPostMeetExcel"))
        .body({ meetId, virtualMeetId })

      download(file, "MeetResults.xlsx")
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error downloading results Excel", err)
      message.error("Something went wrong generating the results Excel file.")
    }
  }

  const buildMeetPdfFilename = (
    baseSuffix: string,
  ): string => {
    const safeMeetTitle = (meetTitle || "Meet")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")

    const safeDatePart = (dateForFilename || dateLabel || "")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")

    let filename = baseSuffix
    if (safeMeetTitle && safeDatePart) {
      filename = `${safeMeetTitle}__${safeDatePart}__${baseSuffix}`
    } else if (safeMeetTitle) {
      filename = `${safeMeetTitle}__${baseSuffix}`
    }

    return filename
  }

  const handleDownloadRibbonLabelsPdf = async () => {
    if (!meetId) {
      message.error("Missing meet information for ribbon labels PDF.")
      return
    }

    if (!ribbonLabelsClubIds || ribbonLabelsClubIds.length === 0) {
      message.error("Select at least one club to include for ribbon labels.")
      return
    }

    if (!ribbonLabelsAllPlaces && ribbonLabelsPlacesError) {
      message.error(
        "Please fix the 'Places to include' settings before downloading.",
      )
      return
    }

    const rawStartIndex = ribbonLabelsStartLabel
      ? parseInt(ribbonLabelsStartLabel, 10)
      : NaN
    if (Number.isNaN(rawStartIndex) || rawStartIndex < 1 || rawStartIndex > 30) {
      message.error("Please enter a valid starting label between 1 and 30.")
      setRibbonLabelsStartLabelError("Starting label must be between 1 and 30.")
      return
    }

    try {
      const minPlaceNum =
        typeof ribbonLabelsMinPlace === "number" ? ribbonLabelsMinPlace : undefined
      const maxPlaceNum =
        typeof ribbonLabelsMaxPlace === "number" ? ribbonLabelsMaxPlace : undefined

      const shouldFilterBySwimmer =
        ribbonLabelsSwimmerOptions.length > 0 &&
        ribbonLabelsSwimmerIds.length > 0 &&
        ribbonLabelsSwimmerIds.length < ribbonLabelsSwimmerOptions.length

      const bodyPayload: any = {
        meetId,
        clubIds: ribbonLabelsClubIds,
        startLabelIndex: rawStartIndex,
        sortMode: ribbonLabelsSortMode,
        eventIds: ribbonLabelsEventIds,
        minPlace:
          typeof minPlaceNum === "number" && !Number.isNaN(minPlaceNum)
            ? minPlaceNum
            : undefined,
        maxPlace:
          typeof maxPlaceNum === "number" && !Number.isNaN(maxPlaceNum)
            ? maxPlaceNum
            : undefined,
        includeExhibition: ribbonLabelsIncludeExhibition,
        dqMode: ribbonLabelsDqMode,
        showDqTimes: ribbonLabelsShowDqTimes,
        showDqReasons: ribbonLabelsShowDqReasons,
        showPoints: ribbonLabelsShowPoints,
        placeMode: ribbonLabelsPlaceMode,
        placeCapEnabled: ribbonLabelsPlaceCapEnabled,
        placeCap:
          ribbonLabelsPlaceCapEnabled &&
            typeof ribbonLabelsPlaceCap === "number" &&
            !Number.isNaN(ribbonLabelsPlaceCap)
            ? ribbonLabelsPlaceCap
            : undefined,
        selectedAgeGroups,
        genderFilter,
      }

      if (shouldFilterBySwimmer) {
        bodyPayload.swimmerIds = ribbonLabelsSwimmerIds
      }

      const file = await (swimminglyApi as any)
        .post(gen("/api/createRibbonLabelsPdf"))
        .body(bodyPayload)

      if (!file || (file as any).status === "error" || (file as any).message) {
        const errMessage =
          (file as any)?.message ||
          "Something went wrong generating the ribbon labels PDF."
        message.error(errMessage)
        return
      }

      const filename = buildMeetPdfFilename("RibbonLabels.pdf")

      download(file as any, filename)
    } catch (err) {
      message.error("Something went wrong generating the ribbon labels PDF.")
    }
  }

  const handleDownloadResultsPdf = async () => {
    if (!meetId && !virtualMeetId) {
      message.error("Missing meet information for results PDF.")
      return
    }

    if (!resultsPdfAllPlaces && resultsPdfPlacesError) {
      message.error("Please fix the 'Places to include' settings before downloading.")
      return
    }

    try {
      const minPlace = resultsPdfMinPlace
        ? parseInt(resultsPdfMinPlace, 10)
        : undefined
      const maxPlace = resultsPdfMaxPlace
        ? parseInt(resultsPdfMaxPlace, 10)
        : undefined

      // Debug: log when the download button is actually triggering the API call
      // and what payload we're about to send.
      // eslint-disable-next-line no-console
      console.log("[Results PDF] Download click payload", {
        meetId,
        virtualMeetId,
        clubIds: resultsPdfClubIds,
        swimEventIds: resultsPdfEventIds,
        mode: resultsPdfMode,
        columns: resultsPdfColumns,
        minPlace,
        maxPlace,
        printOnlyFirstHeat: resultsPdfFirstHeatOnly,
        printOnlyLastHeat: resultsPdfLastHeatOnly,
        showTeamScores: resultsPdfShowTeamScores,
        showSwimPoints: resultsPdfShowSwimPoints,
        showDqs: resultsPdfShowDqs,
        showDqTimes: resultsPdfShowDqTimes,
        showDqReasons: resultsPdfShowDqReasons,
        showExhibition: resultsPdfShowExhibition,
      })

      const file = await (swimminglyApi as any)
        .post(gen("/api/createMeetResultsPdf"))
        .body({
          meetId,
          virtualMeetId,
          clubIds: resultsPdfClubIds,
          swimEventIds: resultsPdfEventIds,
          mode: resultsPdfMode,
          columns: resultsPdfColumns,
          minPlace:
            typeof minPlace === "number" && !Number.isNaN(minPlace)
              ? minPlace
              : undefined,
          maxPlace:
            typeof maxPlace === "number" && !Number.isNaN(maxPlace)
              ? maxPlace
              : undefined,
          printOnlyFirstHeat: resultsPdfFirstHeatOnly,
          printOnlyLastHeat: resultsPdfLastHeatOnly,
          showTeamScores: resultsPdfShowTeamScores,
          showSwimPoints: resultsPdfShowSwimPoints,
          showDqs: resultsPdfShowDqs,
          showDqTimes: resultsPdfShowDqTimes,
          showDqReasons: resultsPdfShowDqReasons,
          showExhibition: resultsPdfShowExhibition,
          brandingClubId: activeBrandingClubId || undefined,
          brandingSlotIndex: resultsPdfBrandingSlotIndex,
        })

      const filename = buildMeetPdfFilename("MeetResults.pdf")

      download(file, filename)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error downloading results PDF", err)
      message.error("Something went wrong generating the results PDF file.")
    }
  }

  const handleGenerateLineup = async () => {
    if (!meetId) {
      message.error("Missing meet information for lineup report.")
      return
    }
    const clubIdsForLineup: Array<number | string> = (() => {
      if (isEntriesLeagueByEvent) {
        if (selectedLineupClubIds && selectedLineupClubIds.length > 0) {
          return selectedLineupClubIds
        }
        if (selectedClubId) {
          return [selectedClubId]
        }
        return []
      }
      return selectedClubId ? [selectedClubId] : []
    })()

    if (!clubIdsForLineup.length) {
      message.error("Missing club information for lineup report.")
      return
    }
    if (
      lineupViewMode !== "event" &&
      lineupViewMode !== "swimmer" &&
      (!selectedAgeGroups || selectedAgeGroups.length === 0)
    ) {
      message.error("Select at least one age group to include.")
      return
    }

    try {
      const apiGender =
        genderFilter === "girls" ? "female" : genderFilter === "boys" ? "male" : "all"

      const responses = await Promise.all(
        clubIdsForLineup.map((clubIdForReq) =>
          (swimminglyApi as any)
            .post(gen("/api/heatsheetPost"))
            .body({
              clubId: parseInt(String(clubIdForReq), 10),
              meetId,
              ageStart: "null",
              ageEnd: "null",
              gender: apiGender,
            }),
        ),
      )

      const validResponses = responses.filter(
        (resp) => resp && resp.recordsAggregated,
      )
      if (!validResponses.length) {
        message.error("Unable to build lineup data for this meet.")
        return
      }

      const mergedRecordsAggregated = validResponses.flatMap(
        (resp) => resp.recordsAggregated || [],
      )

      const primaryClubId = clubIdsForLineup[0]
      const selectedClubMeta =
        meetClubs && meetClubs.length && primaryClubId
          ? meetClubs.find((c) => String(c.clubId) === String(primaryClubId))
          : null
      const lineupClubName = isEntriesLeagueByEvent && clubIdsForLineup.length > 1
        ? "Multiple Clubs"
        : (selectedClubMeta &&
          (selectedClubMeta.nameLong ||
            selectedClubMeta.club_name_long ||
            selectedClubMeta.club_name ||
            selectedClubMeta.name)) ||
        clubDisplayName

      const allMeetClubIds = (meetClubs || [])
        .map((c) => (c && c.clubId != null ? String(c.clubId) : null))
        .filter((id): id is string => !!id)
      const selectedIdsForLineup = clubIdsForLineup.map((id) => String(id))
      const isAllClubsSelected =
        isEntriesLeagueByEvent &&
        allMeetClubIds.length > 0 &&
        allMeetClubIds.every((id) => selectedIdsForLineup.includes(id))
      const isMultiClub = isEntriesLeagueByEvent && clubIdsForLineup.length > 1

      generateLineupPDF(
        {
          recordsAggregated: mergedRecordsAggregated,
          meetName: validResponses[0].meetName,
          meetDate: validResponses[0].meetDate,
        },
        {
          columns: lineupColumns,
          includeTimes,
          includeNotes,
          selectedAgeGroups,
          genderFilter,
          clubName: lineupClubName,
          onlyRelays,
          openOnlyMeet: isOpenOnlyMeet,
          viewMode: lineupViewMode,
          includeHeatLane: context !== "entries",
          multiClub: isMultiClub,
          meetPsychSheet: isAllClubsSelected,
          logoDataUri: selectedLineupBrandingOption?.dataUri || null,
          logoWidth: selectedLineupBrandingOption?.imageWidth || null,
          logoHeight: selectedLineupBrandingOption?.imageHeight || null,
        },
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error generating lineup report", err)
      message.error("Something went wrong generating the lineup report.")
    }
  }

  // Auto-generate previews for the selected report when options change.

  // Heat Sheet preview
  useEffect(() => {
    if (!isActive) return
    if (selectedReport !== "heatSheet") return
    if (!meetId || !club || !club.clubId) return

    const cols = heatSheetColumns === 2 ? 2 : 3
    let cancelled = false

    const run = async () => {
      setPreviewLoading(true)
      setHeatSheetPreviewEmpty(false)
      try {
        const response = await fetch(
          `/api/getHeatSheetJson/meet=${meetId}/club=${club.clubId}`,
          {
            method: "get",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
          },
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`)
        }

        const data = await response.json()
        if (!data.heatSheetData || !Array.isArray(data.heatSheetData.events)) {
          throw new Error(
            "Invalid data format: Missing or invalid 'heatSheetData' or 'events'",
          )
        }

        if (data.heatSheetData.events.length === 0) {
          if (!cancelled) {
            setPreviewUrl(null)
            setHeatSheetPreviewEmpty(true)
          }
          return
        }

        const url = (generateHeatSheetPDF(data.heatSheetData, {}, cols, {
          mode: "previewOnly",
          logoDataUri: selectedHeatSheetBrandingOption?.dataUri || null,
          logoWidth: selectedHeatSheetBrandingOption?.imageWidth || null,
          logoHeight: selectedHeatSheetBrandingOption?.imageHeight || null,
        }) as unknown) as string | null
        if (!cancelled) {
          if (url) {
            setPreviewUrl(url)
            setHeatSheetPreviewEmpty(false)
          } else {
            setPreviewUrl(null)
            setHeatSheetPreviewEmpty(true)
          }
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating heat sheet preview", err)
          message.error("Unable to build heat sheet preview for this meet.")
          setPreviewUrl(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    isActive,
    selectedReport,
    heatSheetColumns,
    meetId,
    club,
    selectedHeatSheetBrandingOption,
  ])

  // Lineup preview
  useEffect(() => {
    if (!isActive) return
    if (selectedReport !== "lineup") return
    if (!meetId || !selectedClubId) return
    if (
      lineupViewMode !== "event" &&
      lineupViewMode !== "swimmer" &&
      (!selectedAgeGroups || selectedAgeGroups.length === 0) &&
      ageGroupOptions.length > 0
    ) {
      // When we know there are specific age groups, require at least one
      // to be selected; but if no age group metadata is available (e.g. in
      // some entry contexts), allow the preview to load without this filter.
      return
    }

    let cancelled = false
    const run = async () => {
      setPreviewLoading(true)
      setLineupPreviewEmpty(false)
      try {
        if (
          !isEntriesLeagueByEvent &&
          selectedClubEntriesCount !== null &&
          selectedClubEntriesCount === 0
        ) {
          if (!cancelled) {
            setPreviewUrl(null)
            setLineupPreviewEmpty(true)
          }
          return
        }

        const apiGender =
          genderFilter === "girls"
            ? "female"
            : genderFilter === "boys"
              ? "male"
              : "all"
        const clubIdsForPreview: Array<number | string> = (() => {
          if (isEntriesLeagueByEvent) {
            if (selectedLineupClubIds && selectedLineupClubIds.length > 0) {
              return selectedLineupClubIds
            }
            if (selectedClubId) {
              return [selectedClubId]
            }
            return []
          }
          return selectedClubId ? [selectedClubId] : []
        })()

        if (!clubIdsForPreview.length) {
          if (!cancelled) {
            setPreviewUrl(null)
            setLineupPreviewEmpty(false)
          }
          return
        }

        const responses = await Promise.all(
          clubIdsForPreview.map((clubIdForReq) =>
            (swimminglyApi as any)
              .post(gen("/api/heatsheetPost"))
              .body({
                clubId: parseInt(String(clubIdForReq), 10),
                meetId,
                ageStart: "null",
                ageEnd: "null",
                gender: apiGender,
              }),
          ),
        )

        const validResponses = responses.filter(
          (resp) => resp && resp.recordsAggregated,
        )
        if (!validResponses.length) {
          if (!cancelled) {
            message.error("Unable to build lineup data for this meet.")
            setPreviewUrl(null)
          }
          return
        }

        const mergedRecordsAggregated = validResponses.flatMap(
          (resp) => resp.recordsAggregated || [],
        )

        if (mergedRecordsAggregated.length === 0) {
          if (!cancelled) {
            setPreviewUrl(null)
            setLineupPreviewEmpty(true)
          }
          return
        }

        const primaryClubId = clubIdsForPreview[0]
        const selectedClubMeta =
          meetClubs && meetClubs.length && primaryClubId
            ? meetClubs.find((c) => String(c.clubId) === String(primaryClubId))
            : null
        const lineupClubName =
          isEntriesLeagueByEvent && clubIdsForPreview.length > 1
            ? "Multiple Clubs"
            : (selectedClubMeta &&
              (selectedClubMeta.nameLong ||
                selectedClubMeta.club_name_long ||
                selectedClubMeta.club_name ||
                selectedClubMeta.name)) ||
            clubDisplayName

        const allMeetClubIds = (meetClubs || [])
          .map((c) => (c && c.clubId != null ? String(c.clubId) : null))
          .filter((id): id is string => !!id)
        const selectedIdsForPreview = clubIdsForPreview.map((id) => String(id))
        const isAllClubsSelectedPreview =
          isEntriesLeagueByEvent &&
          allMeetClubIds.length > 0 &&
          allMeetClubIds.every((id) => selectedIdsForPreview.includes(id))
        const isMultiClubPreview =
          isEntriesLeagueByEvent && clubIdsForPreview.length > 1

        const url = (generateLineupPDF(
          {
            recordsAggregated: mergedRecordsAggregated,
            meetName: validResponses[0].meetName,
            meetDate: validResponses[0].meetDate,
          },
          {
            columns: lineupColumns,
            includeTimes,
            includeNotes,
            selectedAgeGroups,
            genderFilter,
            clubName: lineupClubName,
            onlyRelays,
            openOnlyMeet: isOpenOnlyMeet,
            mode: "previewOnly",
            viewMode: lineupViewMode,
            includeHeatLane: context !== "entries",
            multiClub: isMultiClubPreview,
            meetPsychSheet: isAllClubsSelectedPreview,
            logoDataUri: selectedLineupBrandingOption?.dataUri || null,
            logoWidth: selectedLineupBrandingOption?.imageWidth || null,
            logoHeight: selectedLineupBrandingOption?.imageHeight || null,
          },
        ) as unknown) as string | null
        if (!cancelled && url) {
          setPreviewUrl(url)
          setLineupPreviewEmpty(false)
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating lineup preview", err)
          message.error("Something went wrong generating the lineup preview.")
          setPreviewUrl(null)
          setLineupPreviewEmpty(false)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    isActive,
    selectedReport,
    meetId,
    selectedClubId,
    selectedAgeGroups,
    genderFilter,
    lineupColumns,
    includeTimes,
    includeNotes,
    onlyRelays,
    meetClubs,
    clubDisplayName,
    context,
    lineupViewMode,
    isEntriesLeagueByEvent,
    selectedLineupClubIds,
    isOpenOnlyMeet,
    ageGroupOptions,
    selectedClubEntriesCount,
    selectedLineupBrandingOption,
  ])

  // Meet Entry Summary preview
  useEffect(() => {
    if (!isActive) return
    if (selectedReport !== "entrySummary") return
    if (!meetId) return
    if (!summarySelectedClubIds || summarySelectedClubIds.length === 0) return

    let cancelled = false
    const run = async () => {
      setPreviewLoading(true)
      try {
        const data = await (swimminglyApi as any)
          .post(gen("/api/meetEntrySummaryPost"))
          .body({
            meetId,
            clubIds: summarySelectedClubIds,
            eventType: summaryEventType,
          })

        if (!data || !data.eventsSummary) {
          if (!cancelled) {
            message.error("Unable to build meet entry summary for this meet.")
            setPreviewUrl(null)
          }
          return
        }

        const url = (generateMeetEntrySummaryPDF(
          {
            eventsSummary: data.eventsSummary,
            meetName: data.meetName,
            meetDate: data.meetDate,
            meetTotalsAllClubs: data.meetTotalsAllClubs,
          },
          {
            columns: summaryColumns,
            mode: "previewOnly",
            logoDataUri: selectedEntrySummaryBrandingOption?.dataUri || null,
            logoWidth: selectedEntrySummaryBrandingOption?.imageWidth || null,
            logoHeight: selectedEntrySummaryBrandingOption?.imageHeight || null,
          },
        ) as unknown) as string | null
        if (!cancelled && url) {
          setPreviewUrl(url)
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating meet entry summary preview", err)
          message.error(
            "Something went wrong generating the meet entry summary preview.",
          )
          setPreviewUrl(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    isActive,
    selectedReport,
    meetId,
    summarySelectedClubIds,
    summaryEventType,
    summaryColumns,
    selectedEntrySummaryBrandingOption,
  ])

  // Meet Swimmer Summary preview
  useEffect(() => {
    if (!isActive) return
    if (selectedReport !== "swimmerSummary") return
    if (!meetId) return
    if (!swimmerSummarySelectedClubIds || swimmerSummarySelectedClubIds.length === 0) return

    let cancelled = false
    const run = async () => {
      setPreviewLoading(true)
      try {
        const data = await (swimminglyApi as any)
          .post(gen("/api/meetSwimmerSummaryPost"))
          .body({
            meetId,
            clubIds: swimmerSummarySelectedClubIds,
            selectedAgeGroups,
            genderFilter,
            genderGrouping: swimmerSummaryGenderGrouping,
          })

        if (!data || !data.clubSummary) {
          if (!cancelled) {
            message.error("Unable to build meet swimmer summary for this meet.")
            setPreviewUrl(null)
          }
          return
        }

        const url = (generateMeetSwimmerSummaryPDF(
          {
            clubSummary: data.clubSummary,
            ageGroupBreakdown: data.ageGroupBreakdown,
            meetName: data.meetName,
            meetDate: data.meetDate,
            meetTotalsAllClubs: data.meetTotalsAllClubs,
          },
          {
            columns: swimmerSummaryColumns,
            mode: "previewOnly",
            logoDataUri: selectedSwimmerSummaryBrandingOption?.dataUri || null,
            logoWidth: selectedSwimmerSummaryBrandingOption?.imageWidth || null,
            logoHeight: selectedSwimmerSummaryBrandingOption?.imageHeight || null,
          },
        ) as unknown) as string | null
        if (!cancelled && url) {
          setPreviewUrl(url)
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating meet swimmer summary preview", err)
          message.error(
            "Something went wrong generating the meet swimmer summary preview.",
          )
          setPreviewUrl(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    isActive,
    selectedReport,
    meetId,
    swimmerSummarySelectedClubIds,
    selectedAgeGroups,
    genderFilter,
    swimmerSummaryGenderGrouping,
    swimmerSummaryColumns,
    selectedSwimmerSummaryBrandingOption,
  ])

  // Best Time Matrix preview (CSV-style snapshot)
  useEffect(() => {
    if (!isActive) return
    if (context !== "entries") return
    if (selectedReport !== "bestTimes") return
    if (!meetId || !selectedClubId) return

    let cancelled = false
    const run = async () => {
      setPreviewLoading(true)
      try {
        const data = await (swimminglyApi as any)
          .post(gen("/api/bestTimesMatrixPost"))
          .body({
            meetId,
            clubId: parseInt(String(selectedClubId), 10),
          })

        if (!data || !Array.isArray(data.results) || data.results.length === 0) {
          if (!cancelled) {
            setBestTimesPreviewRows([])
          }
          return
        }

        if (!cancelled) {
          const sorted = [...data.results].sort((a, b) => {
            const agA = String(a.eventAgeGroup || "")
            const agB = String(b.eventAgeGroup || "")
            if (agA !== agB) return agA.localeCompare(agB)
            const gA = String(a.eventGender || "")
            const gB = String(b.eventGender || "")
            if (gA !== gB) return gA.localeCompare(gB)
            const lA = String(a.lastName || "")
            const lB = String(b.lastName || "")
            if (lA !== lB) return lA.localeCompare(lB)
            const fA = String(a.firstName || "")
            const fB = String(b.firstName || "")
            return fA.localeCompare(fB)
          })
          setBestTimesPreviewRows(sorted.slice(0, 25))
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating Best Time Matrix preview", err)
          setBestTimesPreviewRows(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [isActive, context, selectedReport, meetId, selectedClubId])

  // Results Excel preview (JSON snapshot of the three tabs)
  useEffect(() => {
    if (!isActive) return
    if (context !== "results") return
    if (selectedReport !== "resultsExcel") return
    if (!meetId && !virtualMeetId) return

    let cancelled = false

    const run = async () => {
      setPreviewLoading(true)
      setResultsExcelPreview(null)
      setPreviewUrl(null)

      try {
        const data = await (swimminglyApi as any)
          .post(gen("/api/postMeetResultsPreview"))
          .body({ meetId, virtualMeetId })

        if (cancelled) return

        if (!data || (data as any).status === "error") {
          setResultsExcelPreview(null)
          return
        }

        const scores = Array.isArray((data as any).scores)
          ? (data as any).scores
          : []
        const swimmerResults = Array.isArray((data as any).swimmerResults)
          ? (data as any).swimmerResults
          : []
        const highPoints = Array.isArray((data as any).highPoints)
          ? (data as any).highPoints
          : []

        setResultsExcelPreview({ scores, swimmerResults, highPoints })
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating results Excel preview", err)
          setResultsExcelPreview(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [isActive, context, selectedReport, meetId, virtualMeetId])

  // Results PDF preview (server-rendered PDF via iframe)
  useEffect(() => {
    if (!isActive) return
    if (context !== "results") return
    if (selectedReport !== "resultsPdf") return
    if (!meetId && !virtualMeetId) return
    if (!resultsPdfAllPlaces && resultsPdfPlacesError) return

    let cancelled = false

    const run = async () => {
      setPreviewLoading(true)
      setPreviewUrl(null)

      try {
        const minPlace = resultsPdfMinPlace
          ? parseInt(resultsPdfMinPlace, 10)
          : undefined
        const maxPlace = resultsPdfMaxPlace
          ? parseInt(resultsPdfMaxPlace, 10)
          : undefined

        const bodyPayload: any = {
          meetId,
          virtualMeetId,
          clubIds: resultsPdfClubIds,
          swimEventIds: resultsPdfEventIds,
          mode: resultsPdfMode,
          columns: resultsPdfColumns,
          minPlace:
            typeof minPlace === "number" && !Number.isNaN(minPlace)
              ? minPlace
              : undefined,
          maxPlace:
            typeof maxPlace === "number" && !Number.isNaN(maxPlace)
              ? maxPlace
              : undefined,
          printOnlyFirstHeat: resultsPdfFirstHeatOnly,
          printOnlyLastHeat: resultsPdfLastHeatOnly,
          showTeamScores: resultsPdfShowTeamScores,
          showSwimPoints: resultsPdfShowSwimPoints,
          showDqs: resultsPdfShowDqs,
          showDqTimes: resultsPdfShowDqTimes,
          showDqReasons: resultsPdfShowDqReasons,
          showExhibition: resultsPdfShowExhibition,
          brandingClubId: activeBrandingClubId || undefined,
          brandingSlotIndex: resultsPdfBrandingSlotIndex,
        }

        // Debug: log when the preview effect actually fires and what payload
        // we're posting to the preview endpoint.
        // eslint-disable-next-line no-console
        console.log("[Results PDF] Preview fetch payload", bodyPayload)

        const response = await fetch("/api/createMeetResultsPdf", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Swimmingly-Preview": "1",
          },
          credentials: "same-origin",
          body: JSON.stringify(bodyPayload),
        })

        if (!response.ok) {
          throw new Error(
            `Failed to fetch results PDF preview: ${response.status}`,
          )
        }

        const blob = await response.blob()
        if (cancelled) return

        const url = URL.createObjectURL(blob)
        setPreviewUrl(url)
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating results PDF preview", err)
          setPreviewUrl(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    isActive,
    context,
    selectedReport,
    meetId,
    virtualMeetId,
    resultsPdfClubIds,
    resultsPdfEventIds,
    resultsPdfMode,
    resultsPdfColumns,
    resultsPdfMinPlace,
    resultsPdfMaxPlace,
    resultsPdfFirstHeatOnly,
    resultsPdfLastHeatOnly,
    resultsPdfShowTeamScores,
    resultsPdfShowSwimPoints,
    resultsPdfShowDqs,
    resultsPdfShowDqTimes,
    resultsPdfShowDqReasons,
    resultsPdfShowExhibition,
    resultsPdfAllPlaces,
    resultsPdfPlacesError,
    resultsPdfBrandingSlotIndex,
  ])

  const renderBrandingControls = (
    value: number,
    onChange: (value: number) => void,
  ) => (
    <>
      <Divider style={{ margin: "8px 0" }}>Branding</Divider>
      {reportBrandingLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : reportBrandingOptions.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>Choose logo for this report</div>
          <Select
            style={{ width: "100%" }}
            value={value}
            onChange={(nextValue) => onChange(Number(nextValue))}
            options={[
              { value: NO_LOGO_BRANDING_VALUE, label: "No Logo" },
              ...reportBrandingOptions.map((logo) => ({
                value: logo.slotIndex,
                label: `${logo.slotLabel} - ${logo.displayName}`,
              })),
            ]}
          />
          <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
            Brand this report with {reportBrandingClubName}&apos;s approved logo, or choose No Logo. When you are aliased into a club, report branding follows that club context.
          </Paragraph>
        </div>
      ) : (
        <Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 16 }}>
          Add and approve a team logo in Team Logos to brand this report. Once approved, your current club can choose Primary, Secondary, or Alternate branding here.
        </Paragraph>
      )}
    </>
  )

  // Ribbon Labels PDF preview (server-rendered PDF via iframe)
  useEffect(() => {
    if (!isActive) return
    if (context !== "results") return
    if (selectedReport !== "ribbonLabelsPdf") return
    if (!meetId) return
    if (!ribbonLabelsClubIds || ribbonLabelsClubIds.length === 0) return
    if (!ribbonLabelsAllPlaces && ribbonLabelsPlacesError) return

    let cancelled = false

    const run = async () => {
      setPreviewLoading(true)
      setPreviewUrl(null)

      try {
        const minPlace =
          typeof ribbonLabelsMinPlace === "number"
            ? ribbonLabelsMinPlace
            : undefined
        const maxPlace =
          typeof ribbonLabelsMaxPlace === "number"
            ? ribbonLabelsMaxPlace
            : undefined

        const bodyPayload: any = {
          meetId,
          clubIds: ribbonLabelsClubIds,
          startLabelIndex: ribbonLabelsStartLabel
            ? parseInt(ribbonLabelsStartLabel, 10)
            : 1,
          sortMode: ribbonLabelsSortMode,
          eventIds: ribbonLabelsEventIds,
          minPlace:
            typeof minPlace === "number" && !Number.isNaN(minPlace)
              ? minPlace
              : undefined,
          maxPlace:
            typeof maxPlace === "number" && !Number.isNaN(maxPlace)
              ? maxPlace
              : undefined,
          includeExhibition: ribbonLabelsIncludeExhibition,
          dqMode: ribbonLabelsDqMode,
          showDqTimes: ribbonLabelsShowDqTimes,
          showDqReasons: ribbonLabelsShowDqReasons,
          showPoints: ribbonLabelsShowPoints,
          placeMode: ribbonLabelsPlaceMode,
          placeCapEnabled: ribbonLabelsPlaceCapEnabled,
          placeCap:
            ribbonLabelsPlaceCapEnabled &&
              typeof ribbonLabelsPlaceCap === "number" &&
              !Number.isNaN(ribbonLabelsPlaceCap)
              ? ribbonLabelsPlaceCap
              : undefined,
          selectedAgeGroups,
          genderFilter,
        }

        const shouldFilterBySwimmer =
          ribbonLabelsSwimmerOptions.length > 0 &&
          ribbonLabelsSwimmerIds.length > 0 &&
          ribbonLabelsSwimmerIds.length < ribbonLabelsSwimmerOptions.length

        if (shouldFilterBySwimmer) {
          bodyPayload.swimmerIds = ribbonLabelsSwimmerIds
        }

        const response = await fetch("/api/createRibbonLabelsPdf", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Swimmingly-Preview": "1",
          },
          credentials: "same-origin",
          body: JSON.stringify(bodyPayload),
        })

        if (!response.ok) {
          throw new Error(
            `Failed to fetch ribbon labels PDF preview: ${response.status}`,
          )
        }

        const blob = await response.blob()
        if (cancelled) return

        const url = URL.createObjectURL(blob)
        setPreviewUrl(url)
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Error generating ribbon labels PDF preview", err)
          setPreviewUrl(null)
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    isActive,
    context,
    selectedReport,
    meetId,
    ribbonLabelsClubIds,
    ribbonLabelsStartLabel,
    ribbonLabelsSortMode,
    ribbonLabelsEventIds,
    ribbonLabelsMinPlace,
    ribbonLabelsMaxPlace,
    ribbonLabelsAllPlaces,
    ribbonLabelsPlacesError,
    ribbonLabelsIncludeExhibition,
    ribbonLabelsDqMode,
    ribbonLabelsShowDqTimes,
    ribbonLabelsShowDqReasons,
    ribbonLabelsShowPoints,
    ribbonLabelsPlaceMode,
    ribbonLabelsPlaceCapEnabled,
    ribbonLabelsPlaceCap,
    selectedAgeGroups,
    genderFilter,
    ribbonLabelsSwimmerIds,
  ])

  useEffect(() => {
    if (!hyvModalVisible) return
    if (hyvFacilityName) return

    let homeClubName = ""
    if (meetInfoArr && (meetInfoArr as any).is_home && meetClubs && meetClubs.length) {
      const homeClubId = (meetInfoArr as any).is_home
      const homeClub = meetClubs.find(
        (c) => String(c.clubId) === String(homeClubId),
      )
      if (homeClub) {
        homeClubName =
          homeClub.nameLong ||
          homeClub.club_name_long ||
          homeClub.club_name ||
          homeClub.name ||
          ""
      }
    }

    const poolAddress =
      (meetInfoArr && (meetInfoArr as any).location) || ""

    const combined = `${homeClubName} ${poolAddress}`.trim()

    if (combined) {
      setHyvFacilityName(combined)
    } else if (meetTitle) {
      setHyvFacilityName(meetTitle)
    }
  }, [hyvModalVisible, hyvFacilityName, meetInfoArr, meetClubs, meetTitle])

  // Initialise entry limit values from the meet's saved limits each time the modal opens
  useEffect(() => {
    if (!hyvModalVisible) return
    setEv3LimitsEnabled(false)
    setEv3IndLimit(meetInfoArr?.individualLimit ?? null)
    setEv3RelayLimit(meetInfoArr?.relayLimit ?? null)
    setEv3TotalLimit(meetInfoArr?.totalLimit ?? null)
  }, [hyvModalVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  const getCreateReportLabel = (): string => {
    if (context === "results" && selectedReport === "resultsExport") {
      return "Create Results Export File"
    }
    if (context === "results" && selectedReport === "resultsExcel") {
      return "Create Results Spreadsheet"
    }
    if (context === "results" && selectedReport === "resultsPdf") {
      return "Create Results PDF"
    }
    if (context === "results" && selectedReport === "resultsPdfLegacy") {
      return "Download Legacy Results PDF"
    }
    if (context === "results" && selectedReport === "labelsPdfLegacy") {
      return "Download Legacy Labels PDF"
    }
    if (context === "results" && selectedReport === "ribbonLabelsPdf") {
      return "Create Ribbon Labels PDF"
    }
    if (context === "entries" && selectedReport === "lineup") {
      return "Create Lineup Report"
    }
    if (context === "entries" && selectedReport === "bestTimes") {
      return "Create Best Time Matrix"
    }
    if (context === "entries" && selectedReport === "exportEntries") {
      return "Create Entries Export File"
    }
    if (context === "entries" && selectedReport === "exportMeetEvents") {
      return "Create Meet Events File"
    }
    if (context !== "entries" && selectedReport === "heatSheet") {
      return "Create Heat Sheet"
    }
    if (context !== "entries" && selectedReport === "lineup") {
      return "Create Lineup Report"
    }
    if (context !== "entries" && selectedReport === "entrySummary") {
      return "Create Meet Summary"
    }
    if (context !== "entries" && selectedReport === "swimmerSummary") {
      return "Create Meet Summary PDF"
    }
    return "Create Report"
  }

  const handleOpenPreviewDrawer = () => {
    if (!isPreviewableSelection) return
    setPreviewDrawerVisible(true)
  }

  const runReportAction = async () => {
    if (context === "results") {
      if (selectedReport === "resultsExport") {
        if (onOpenExportResults) {
          onOpenExportResults()
        } else {
          message.error("Export Meet Results is not available here.")
        }
        return
      }
      if (selectedReport === "resultsExcel") {
        await handleDownloadResultsExcel()
        return
      }
      if (selectedReport === "resultsPdf") {
        await handleDownloadResultsPdf()
        return
      }
      if (selectedReport === "resultsPdfLegacy") {
        if (!meetId || !club || !club.clubId) {
          message.error("Missing meet or club information for PDF download.")
          return
        }
        if (!resultsPdfExists) {
          message.error("No legacy results PDF is available for this meet.")
          return
        }
        const file = await (swimminglyApi as any).get(
          gen(`/api/downloadPdf/${meetId}/false/${club.clubId}`),
        )
        download(file, "MeetResults.pdf")
        return
      }
      if (selectedReport === "labelsPdfLegacy") {
        if (!meetId || !club || !club.clubId) {
          message.error("Missing meet or club information for PDF download.")
          return
        }
        if (!labelsPdfExists) {
          message.error("No legacy labels PDF is available for this meet.")
          return
        }
        const file = await (swimminglyApi as any).get(
          gen(`/api/downloadPdf/${meetId}/true/${club.clubId}`),
        )
        download(file, "Labels.pdf")
        return
      }
      if (selectedReport === "ribbonLabelsPdf") {
        await handleDownloadRibbonLabelsPdf()
        return
      }
    } else if (context === "entries") {
      if (selectedReport === "lineup") {
        await handleGenerateLineup()
        return
      }
      if (selectedReport === "bestTimes") {
        await handleGenerateBestTimesMatrix()
        return
      }
      if (selectedReport === "exportEntries") {
        if (onOpenExportEntries) {
          const defaultIds = (meetClubs || []).map((c) => c.clubId)
          const idsToUse =
            exportSelectedClubIds && exportSelectedClubIds.length
              ? exportSelectedClubIds
              : defaultIds

          if (!idsToUse || !idsToUse.length) {
            message.error("No clubs available to export.")
            return
          }

          onOpenExportEntries(idsToUse)
        } else {
          message.error("Export Meet Entries is not available here.")
        }
        return
      }
      if (selectedReport === "exportMeetEvents") {
        if (!meetId) {
          message.error("Missing meet information for export.")
          return
        }
        setExportEventsModalVisible(true)
        return
      }
    } else {
      if (selectedReport === "heatSheet") {
        await handleGenerateHeatSheet()
        return
      }
      if (selectedReport === "lineup") {
        await handleGenerateLineup()
        return
      }
      if (selectedReport === "entrySummary") {
        await handleGenerateEntrySummary()
        return
      }
      if (selectedReport === "swimmerSummary") {
        await handleGenerateSwimmerSummary()
        return
      }
    }
  }

  const runPreviewableReportDownload = async () => {
    if (!isPreviewableSelection) return
    await runReportAction()
  }

  const handlePreviewDrawerDownload = async () => {
    if (!isPreviewableSelection) return

    setPreviewDrawerDownloadLoading(true)
    try {
      await runPreviewableReportDownload()
      message.success("Report download started.")
      setPreviewDrawerVisible(false)
    } finally {
      setPreviewDrawerDownloadLoading(false)
    }
  }

  const handleSecondaryFooterClick = useCallback(async () => {
    // On mobile, previewable reports open the drawer; everything else runs directly.
    if (isMobile && isPreviewableSelection) {
      handleOpenPreviewDrawer()
      return
    }

    setDownloadLoading(true)
    try {
      await runReportAction()
      if (isPreviewableSelection) {
        message.success("Report download started.")
      }
    } finally {
      setDownloadLoading(false)
    }
  }, [isMobile, isPreviewableSelection, runReportAction])

  const latestInlineCreateHandlerRef = useRef<() => Promise<void>>(() => Promise.resolve())

  useEffect(() => {
    latestInlineCreateHandlerRef.current = handleSecondaryFooterClick
  }, [handleSecondaryFooterClick])

  const handleInlineCreateReportClick = useCallback(() => {
    void latestInlineCreateHandlerRef.current()
  }, [])

  const renderSecondaryFooterButton = () => {
    return (
      <Button type="default" onClick={handleSecondaryFooterClick}>
        {getCreateReportLabel()}
      </Button>
    )
  }

  // When the studio is rendered inline (inside another modal), surface
  // the current Create Report action up to the parent so it can render
  // the button in its own toolbar/footer instead of inside the scrollable
  // body content.
  useLayoutEffect(() => {
    if (!inline || !onInlineCreateReportChange) return

    onInlineCreateReportChange({
      label: getCreateReportLabel(),
      onClick: handleInlineCreateReportClick,
    })
  }, [
    context,
    getCreateReportLabel,
    handleInlineCreateReportClick,
    inline,
    onInlineCreateReportChange,
    selectedReport,
  ])

  const renderPreviewContent = () => (
    <>
      {previewLoading ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : selectedReport === "heatSheet" && heatSheetPreviewEmpty ? (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          There are currently no heat sheets available for this meet.
        </Paragraph>
      ) : selectedReport === "lineup" && lineupPreviewEmpty ? (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          No entries yet.
        </Paragraph>
      ) : previewUrl ? (
        <iframe
          title="Report preview"
          src={previewUrl}
          style={{
            width: "100%",
            height: 520,
            border: "1px solid #ddd",
          }}
        />
      ) : (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Adjust options on the left to see a live PDF preview here, then
          click Generate &amp; Download to save a copy.
        </Paragraph>
      )}
    </>
  )

  const renderResultsExcelPreview = () => (
    <>
      {previewLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : resultsExcelPreview ? (
        <div
          style={{
            maxHeight: 520,
            overflow: "auto",
            border: "1px solid #ddd",
            padding: 8,
            fontSize: 11,
          }}
        >
          {/* Team Scores tab */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 4,
                fontSize: 12,
              }}
            >
              Team Scores (sample)
            </div>
            {resultsExcelPreview.scores &&
              resultsExcelPreview.scores.length > 0 ? (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: 4,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      Team
                    </th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>
                      Points
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resultsExcelPreview.scores
                    .slice(0, 8)
                    .map((row: any, idx: number) => (
                      <tr key={idx}>
                        <td style={{ padding: "4px 6px" }}>
                          {row.teamName ||
                            row.team ||
                            row.clubName ||
                            row.club ||
                            ""}
                        </td>
                        <td
                          style={{
                            padding: "4px 6px",
                            textAlign: "right",
                          }}
                        >
                          {row.points != null ? row.points : row.totalPoints}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <Paragraph type="secondary" style={{ fontSize: 11 }}>
                No team scores were available for preview.
              </Paragraph>
            )}
          </div>

          {/* Swimmer Results tab */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 4,
                fontSize: 12,
              }}
            >
              Swimmer Results (sample)
            </div>
            {resultsExcelPreview.swimmerResults &&
              resultsExcelPreview.swimmerResults.length > 0 ? (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: 4,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      Swimmer
                    </th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      Event
                    </th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      Time
                    </th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      DQ
                    </th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      +/-
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resultsExcelPreview.swimmerResults
                    .slice(0, 12)
                    .map((row: any, idx: number) => (
                      <tr key={idx}>
                        <td style={{ padding: "4px 6px" }}>
                          {(row.firstName || row.first_name || "") +
                            " " +
                            (row.lastName || row.last_name || "")}
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                          {row.eventName || row.event || ""}
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                          {row.time || row.finalTime || ""}
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                          {row.dqCode || row.dq || ""}
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                          {row.timeImprovement || row.delta || ""}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <Paragraph type="secondary" style={{ fontSize: 11 }}>
                No swimmer results were available for preview.
              </Paragraph>
            )}
          </div>

          {/* High Points tab */}
          <div>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 4,
                fontSize: 12,
              }}
            >
              High Point Scoring (sample)
            </div>
            {resultsExcelPreview.highPoints &&
              resultsExcelPreview.highPoints.length > 0 ? (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: 4,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      Age Group
                    </th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      Swimmer
                    </th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>
                      Points
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resultsExcelPreview.highPoints
                    .slice(0, 10)
                    .map((row: any, idx: number) => (
                      <tr key={idx}>
                        <td style={{ padding: "4px 6px" }}>
                          {row.ageGroup || row.age_group || ""}
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                          {(row.firstName || row.first_name || "") +
                            " " +
                            (row.lastName || row.last_name || "")}
                        </td>
                        <td
                          style={{
                            padding: "4px 6px",
                            textAlign: "right",
                          }}
                        >
                          {row.points != null ? row.points : row.totalPoints}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <Paragraph type="secondary" style={{ fontSize: 11 }}>
                No high point data were available for preview.
              </Paragraph>
            )}
          </div>

          <Paragraph
            type="secondary"
            style={{ fontSize: 10, marginTop: 8, marginBottom: 0 }}
          >
            Preview shows a subset of rows from each tab; download the spreadsheet
            for the full detail.
          </Paragraph>
        </div>
      ) : (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Unable to build a preview for this Results Excel file, but you can still
          download the spreadsheet below.
        </Paragraph>
      )}
    </>
  )

  const renderBestTimesPreview = () => (
    <>
      {previewLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : bestTimesPreviewRows && bestTimesPreviewRows.length > 0 ? (
        <div
          style={{
            maxHeight: 520,
            overflow: "auto",
            border: "1px solid #ddd",
            padding: 8,
            fontSize: 11,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  Age Group
                </th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  Gender
                </th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  Event
                </th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  Swimmer
                </th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  League Age
                </th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  Swimmer #
                </th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>
                  Best Time
                </th>
              </tr>
            </thead>
            <tbody>
              {bestTimesPreviewRows.map((row: any, idx: number) => (
                <tr key={`${row.swimmer_number || idx}-${idx}`}>
                  <td style={{ padding: "4px 6px" }}>
                    {row.eventAgeGroup || ""} {row.eventGender || ""}
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    {row.eventGender || ""}
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    {row.eventDistance} {row.eventDescription}
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    {row.firstName} {row.lastName}
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    {row.leagueAge}
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    {row.swimmer_number}
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    {row.bestTime}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paragraph
            type="secondary"
            style={{ fontSize: 10, marginTop: 8, marginBottom: 0 }}
          >
            Preview shows a subset of rows; download the CSV for the full Best Time
            Matrix.
          </Paragraph>
        </div>
      ) : bestTimesPreviewRows && bestTimesPreviewRows.length === 0 ? (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Unable to build a preview for this Best Time Matrix, but you can still try
          generating the CSV download.
        </Paragraph>
      ) : (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Select a club on the left to preview the Best Time Matrix before
          downloading the CSV.
        </Paragraph>
      )}
    </>
  )

  const mainContent = (
    <Row gutter={24}>
      {!singleReportMode && (
        <Col xs={24} md={6}>
          <div
            style={{
              paddingRight: 4,
            }}
          >
            <Row gutter={[16, 16]}>
              {context === "results" ? (
                <>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "resultsExcel"}
                      icon={<FontAwesomeIcon icon={faFileSpreadsheet} className="fa-fw" />}
                      title="Meet Results Excel"
                      description="Team scores, raw swimmer results/DQs/time improvements & high point tracking."
                      onClick={() => {
                        setSelectedReport("resultsExcel")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "resultsPdf"}
                      icon={
                        <FontAwesomeIcon icon={faFilePdf} className="fa-fw" />
                      }
                      title="Meet Results PDF"
                      isNew
                      description={
                        <>
                          Customizable swim meet results reports for PDF.
                          <span style={{ display: "block", marginTop: 4 }}>
                            <a
                              href={
                                meetId && club && club.clubId
                                  ? gen(`/api/downloadPdf/${meetId}/false/${club.clubId}`)
                                  : undefined
                              }
                              onClick={(e) => {
                                if (!resultsPdfExists) {
                                  e.preventDefault()
                                }
                              }}
                              style={{
                                color: "#ffffff",
                                cursor: resultsPdfExists ? "pointer" : "not-allowed",
                              }}
                              title={
                                resultsPdfExists
                                  ? "Download legacy Meet Results PDF"
                                  : "File Download Not Available"
                              }
                            >
                              Download legacy Meet Results PDF
                            </a>
                          </span>
                        </>
                      }
                      onClick={() => {
                        setSelectedReport("resultsPdf")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "ribbonLabelsPdf"}
                      icon={<FontAwesomeIcon icon={faFilePdf} className="fa-fw" />}
                      title="Ribbon Labels PDF"
                      isNew
                      description={
                        <>
                          Generate ribbon labels using the 3x10 Avery 5160 layout.
                          <span style={{ display: "block", marginTop: 4 }}>
                            <a
                              href={
                                meetId && club && club.clubId
                                  ? gen(`/api/downloadPdf/${meetId}/true/${club.clubId}`)
                                  : undefined
                              }
                              onClick={(e) => {
                                if (!labelsPdfExists) {
                                  e.preventDefault()
                                }
                              }}
                              style={{
                                color: "#ffffff",
                                cursor: labelsPdfExists ? "pointer" : "not-allowed",
                              }}
                              title={
                                labelsPdfExists
                                  ? "Download legacy Ribbon Labels PDF"
                                  : "File Download Not Available"
                              }
                            >
                              Download legacy Ribbon Labels PDF
                            </a>
                          </span>
                        </>
                      }
                      onClick={() => {
                        setSelectedReport("ribbonLabelsPdf")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "resultsExport"}
                      icon={<FontAwesomeIcon icon={faFileExport} className="fa-fw" />}
                      title="Export Meet Results"
                      description="Export some/all clubs meet results in universally compatible formats."
                      onClick={() => {
                        setSelectedReport("resultsExport")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                </>
              ) : context === "heatSheet" ? (
                <>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "heatSheet"}
                      icon={<FontAwesomeIcon icon={faColumns3} className="fa-fw" />}
                      title="Heat Sheet"
                      description="Choose 2- or 3-column layout and download a heat sheet PDF."
                      onClick={() => {
                        setSelectedReport("heatSheet")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "lineup"}
                      icon={<FontAwesomeIcon icon={faClipboardListCheck} className="fa-fw" />}
                      title="Lineup Report"
                      isNew
                      description="Build a customizable lineup report."
                      onClick={() => {
                        setSelectedReport("lineup")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "entrySummary" || selectedReport === "swimmerSummary"}
                      icon={<FontAwesomeIcon icon={faListOl} className="fa-fw" />}
                      title="Meet Summary"
                      isNew
                      description="Summarize entries or unique swimmers by club for selected teams."
                      onClick={() => {
                        if (selectedReport !== "entrySummary" && selectedReport !== "swimmerSummary") {
                          setSelectedReport("entrySummary")
                          setPreviewUrl(null)
                        }
                      }}
                    />
                  </Col>
                </>
              ) : (
                <>
                  {/* Declarations is a separate view handled outside the main tile list */}
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "lineup"}
                      icon={<FontAwesomeIcon icon={faClipboardListCheck} className="fa-fw" />}
                      title="Psych Sheet Lineup"
                      isNew
                      description="Build a lineup report before heats and lanes are assigned."
                      onClick={() => {
                        setSelectedReport("lineup")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "bestTimes"}
                      icon={<FontAwesomeIcon icon={faFileSpreadsheet} className="fa-fw" />}
                      title="Best Time Matrix"
                      description="Download a CSV of best times by event for this club."
                      onClick={() => {
                        setSelectedReport("bestTimes")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "exportEntries"}
                      icon={
                        <FontAwesomeIcon
                          icon={faFileExport}
                          className="fa-fw"
                        />
                      }
                      title="Export Meet Entries"
                      description="Open the export dialog to create an SD3 entries file."
                      onClick={() => {
                        setSelectedReport("exportEntries")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <ReportTile
                      primary={selectedReport === "exportMeetEvents"}
                      icon={
                        <FontAwesomeIcon
                          icon={faFileExport}
                          className="fa-fw"
                        />
                      }
                      title="Export Meet Events File"
                      isNew
                      description="Export a zip file of your Meet Events File."
                      onClick={() => {
                        setSelectedReport("exportMeetEvents")
                        setPreviewUrl(null)
                      }}
                    />
                  </Col>
                </>
              )}
            </Row>
          </div>
        </Col>
      )}
      <Col
        xs={24}
        md={
          singleReportMode && keepInlinePreviewColumns
            ? 15
            : singleReportMode
              ? 24
              : 9
        }
      >
        {selectedReport === "heatSheet" && context === "heatSheet" && (
          <div>
            {renderBrandingControls(
              heatSheetBrandingSlotIndex,
              setHeatSheetBrandingSlotIndex,
            )}
            <Divider style={{ margin: "8px 0" }}>Layout</Divider>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>Columns per page</div>
              <Radio.Group
                value={heatSheetColumns}
                onChange={(e: any) => setHeatSheetColumns(e.target.value)}
              >
                <Radio value={2}>2 columns</Radio>
                <Radio value={3}>3 columns</Radio>
              </Radio.Group>
            </div>

            <Divider style={{ margin: "16px 0" }} />
          </div>
        )}

        {selectedReport === "lineup" && (
          <div>
            {renderBrandingControls(
              lineupBrandingSlotIndex,
              setLineupBrandingSlotIndex,
            )}
            {context === "entries" && (
              <Paragraph
                type="secondary"
                style={{ fontSize: 12, marginBottom: 8 }}
              >
                This is a psych sheet lineup: heat &amp; lane assignments occur
                once the heat sheet is merged.
              </Paragraph>
            )}
            <Divider style={{ margin: "8px 0" }}>Layout</Divider>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>View</div>
              <Radio.Group
                value={lineupViewMode}
                onChange={(e: any) =>
                  setLineupViewMode(e.target.value as "ageGroup" | "event" | "swimmer")
                }
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="ageGroup">By Age Group</Radio.Button>
                <Radio.Button value="event">By Event</Radio.Button>
                <Radio.Button value="swimmer">By Swimmer</Radio.Button>
              </Radio.Group>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>Columns per page</div>
              <Radio.Group
                value={lineupColumns}
                onChange={(e: any) => setLineupColumns(e.target.value)}
              >
                <Radio value={1}>1 column</Radio>
                <Radio value={2}>2 columns</Radio>
                <Radio value={3}>3 columns</Radio>
              </Radio.Group>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={includeTimes}
                onChange={(e: any) => setIncludeTimes(e.target.checked)}
              >
                Include entry times on lineup
              </Checkbox>
            </div>

            {(lineupViewMode === "ageGroup" || lineupViewMode === "swimmer") && (
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={includeNotes}
                  onChange={(e: any) => setIncludeNotes(e.target.checked)}
                >
                  Show swimmer notes area for each swimmer
                </Checkbox>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={onlyRelays}
                onChange={(e: any) => setOnlyRelays(e.target.checked)}
              >
                Only show relays
              </Checkbox>
            </div>

            <Divider style={{ margin: "8px 0" }}>Club</Divider>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>Choose club:</div>
              <Select
                style={{ width: "100%" }}
                mode={isEntriesLeagueByEvent ? "multiple" : undefined}
                value={
                  isEntriesLeagueByEvent
                    ? selectedLineupClubIds
                    : selectedClubId
                }
                onChange={(val: any) => {
                  if (isEntriesLeagueByEvent) {
                    const next = Array.isArray(val) ? val : val ? [val] : []
                    setSelectedLineupClubIds(next)
                    if (next && next.length > 0) {
                      setSelectedClubId(next[0])
                    }
                  } else {
                    setSelectedClubId(val)
                    const asArray = Array.isArray(val)
                      ? val
                      : val
                        ? [val]
                        : []
                    setSelectedLineupClubIds(asArray)
                  }
                }}
                disabled={!meetClubs || meetClubs.length === 0}
              >
                {meetClubs.map((c) => {
                  const isUserClub =
                    !!user && String(c.clubId) === String(user.club_id)
                  const roleName =
                    user && user.role !== undefined
                      ? (roleMapping as any)[user.role]
                      : null
                  const canSelectAllClubs =
                    roleName === "League Admin" ||
                    roleName === "Hydro Admin" ||
                    (user && user.role === 2)
                  const disabled = !canSelectAllClubs && !isUserClub
                  const label =
                    c.nameLong ||
                    c.club_name_long ||
                    c.club_name ||
                    c.name ||
                    c.clubId
                  const isSelected = isEntriesLeagueByEvent
                    ? selectedLineupClubIds.some(
                      (id) => String(id) === String(c.clubId),
                    )
                    : String(selectedClubId) === String(c.clubId)
                  return (
                    <Option key={c.clubId} value={c.clubId} disabled={disabled}>
                      {disabled &&
                        !isUserClub &&
                        !(isSelected && !canSelectAllClubs) ? (
                        <Tooltip title="League Admins only.">
                          <span
                            style={{
                              color: isSelected ? undefined : "#999",
                              fontWeight: isSelected ? 600 : 400,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {isSelected && (
                              <span style={{ marginRight: 6 }}>✓</span>
                            )}
                            {label}
                          </span>
                        </Tooltip>
                      ) : (
                        <span
                          style={{
                            fontWeight: isSelected ? 600 : 400,
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          {isSelected && (
                            <span style={{ marginRight: 6 }}>✓</span>
                          )}
                          {label}
                        </span>
                      )}
                    </Option>
                  )
                })}
              </Select>
              <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                Club Admins default to their own club. League Admins can select any
                club in the meet.
              </Paragraph>
            </div>

            {lineupViewMode === "ageGroup" && (
              <>
                <Divider style={{ margin: "8px 0" }}>Age Groups</Divider>
                <div style={{ marginBottom: 8 }}>
                  <Radio.Group
                    value={genderFilter}
                    onChange={(e: any) => setGenderFilter(e.target.value)}
                  >
                    <Radio value="both">Both</Radio>
                    <Radio value="girls">Girls</Radio>
                    <Radio value="boys">Boys</Radio>
                  </Radio.Group>
                </div>

                <Divider style={{ margin: "8px 0" }} />

                <div
                  style={{ maxHeight: 180, overflowY: "auto", paddingRight: 4 }}
                >
                  {isOpenOnlyMeet ? (
                    <>
                      <div style={{ marginBottom: 4 }}>
                        <Checkbox checked disabled>
                          Open
                        </Checkbox>
                      </div>
                      <Paragraph
                        type="secondary"
                        style={{ fontSize: 11, marginTop: 4 }}
                      >
                        Lineup for OPEN only event orders will be displayed
                        alphabetically.
                      </Paragraph>
                    </>
                  ) : ageGroupOptions.length === 0 ? (
                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                      No age groups were detected for this meet.
                    </Paragraph>
                  ) : (
                    <Checkbox.Group
                      style={{ width: "100%" }}
                      value={selectedAgeGroups}
                      onChange={(vals: any) =>
                        setSelectedAgeGroups(vals as string[])
                      }
                    >
                      {ageGroupOptions.map((opt) => (
                        <div key={opt.value} style={{ marginBottom: 4 }}>
                          <Checkbox value={opt.value}>{opt.label}</Checkbox>
                        </div>
                      ))}
                    </Checkbox.Group>
                  )}
                </div>
              </>
            )}

            <Divider style={{ margin: "16px 0" }} />
          </div>
        )}

        {(selectedReport === "entrySummary" || selectedReport === "swimmerSummary") && context === "heatSheet" && (
          <div>
            {!singleReportMode && (
              <>
                <Divider style={{ margin: "8px 0" }}>Summarize By</Divider>
                <div style={{ marginBottom: 12 }}>
                  <Radio.Group
                    value={selectedReport === "swimmerSummary" ? "swimmers" : "entries"}
                    onChange={(e: any) => {
                      setSelectedReport(e.target.value === "swimmers" ? "swimmerSummary" : "entrySummary")
                      setPreviewUrl(null)
                    }}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="entries">Entries</Radio.Button>
                    <Radio.Button value="swimmers">Swimmers</Radio.Button>
                  </Radio.Group>
                </div>
              </>
            )}

            {selectedReport === "entrySummary" && (
              <>
                {renderBrandingControls(
                  entrySummaryBrandingSlotIndex,
                  setEntrySummaryBrandingSlotIndex,
                )}
                <Divider style={{ margin: "8px 0" }}>Layout</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Columns per page</div>
                  <Radio.Group
                    value={summaryColumns}
                    onChange={(e: any) => setSummaryColumns(e.target.value)}
                  >
                    <Radio value={2}>2 columns</Radio>
                    <Radio value={3}>3 columns</Radio>
                  </Radio.Group>
                </div>


                <Divider style={{ margin: "8px 0" }}>Clubs</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Choose clubs to include</div>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    value={summarySelectedClubIds}
                    onChange={(vals: any[]) => setSummarySelectedClubIds(vals)}
                    disabled={!meetClubs || meetClubs.length === 0}
                    maxTagCount="responsive"
                  >
                    {meetClubs.map((c) => {
                      const label =
                        c.nameLong ||
                        c.club_name_long ||
                        c.club_name ||
                        c.name ||
                        c.clubId
                      const isSelected = summarySelectedClubIds.some(
                        (id) => String(id) === String(c.clubId),
                      )
                      return (
                        <Option key={c.clubId} value={c.clubId}>
                          <span
                            style={{
                              fontWeight: isSelected ? 600 : 400,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {isSelected && (
                              <span style={{ marginRight: 6 }}>✓</span>
                            )}
                            {label}
                          </span>
                        </Option>
                      )
                    })}
                  </Select>
                  <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                    Select one or more clubs. Totals include all selected clubs.
                  </Paragraph>
                </div>

                <Divider style={{ margin: "8px 0" }}>Events</Divider>
                <div style={{ marginBottom: 16 }}>
                  <Radio.Group
                    value={summaryEventType}
                    onChange={(e: any) => setSummaryEventType(e.target.value)}
                  >
                    <Radio value="all">All events</Radio>
                    <Radio value="individual">Individual events only</Radio>
                    <Radio value="relay">Relay events only</Radio>
                  </Radio.Group>
                </div>

                <Divider style={{ margin: "16px 0" }} />
                <Button block onClick={handleDownloadSwimmerSummaryCsv}>
                  Download Swimmer Summary CSV
                </Button>
              </>
            )}

            {selectedReport === "swimmerSummary" && (
              <>
                {renderBrandingControls(
                  swimmerSummaryBrandingSlotIndex,
                  setSwimmerSummaryBrandingSlotIndex,
                )}
                <Divider style={{ margin: "8px 0" }}>Layout</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Columns per page</div>
                  <Radio.Group
                    value={swimmerSummaryColumns}
                    onChange={(e: any) => setSwimmerSummaryColumns(e.target.value)}
                  >
                    <Radio value={2}>2 columns</Radio>
                    <Radio value={3}>3 columns</Radio>
                  </Radio.Group>
                </div>

                <Divider style={{ margin: "8px 0" }}>Clubs</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Choose clubs to include</div>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    value={swimmerSummarySelectedClubIds}
                    onChange={(vals: any[]) => setSwimmerSummarySelectedClubIds(vals)}
                    disabled={!meetClubs || meetClubs.length === 0}
                    maxTagCount="responsive"
                  >
                    {meetClubs.map((c) => {
                      const label =
                        c.nameLong ||
                        c.club_name_long ||
                        c.club_name ||
                        c.name ||
                        c.clubId
                      const isSelected = swimmerSummarySelectedClubIds.some(
                        (id) => String(id) === String(c.clubId),
                      )
                      return (
                        <Option key={c.clubId} value={c.clubId}>
                          <span
                            style={{
                              fontWeight: isSelected ? 600 : 400,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {isSelected && (
                              <span style={{ marginRight: 6 }}>✓</span>
                            )}
                            {label}
                          </span>
                        </Option>
                      )
                    })}
                  </Select>
                  <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                    Select one or more clubs. Totals include all selected clubs.
                  </Paragraph>
                </div>

                <Divider style={{ margin: "8px 0" }}>Age Groups</Divider>
                <div style={{ marginBottom: 8 }}>
                  <Radio.Group
                    value={genderFilter}
                    onChange={(e: any) => setGenderFilter(e.target.value)}
                  >
                    <Radio value="both">Both</Radio>
                    <Radio value="girls">Girls</Radio>
                    <Radio value="boys">Boys</Radio>
                  </Radio.Group>
                </div>

                {genderFilter === "both" && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ marginBottom: 4 }}>Gender grouping</div>
                    <Radio.Group
                      value={swimmerSummaryGenderGrouping}
                      onChange={(e: any) => setSwimmerSummaryGenderGrouping(e.target.value)}
                    >
                      <Radio value="combined">Combined</Radio>
                      <Radio value="separate">Separate</Radio>
                    </Radio.Group>
                  </div>
                )}

                <Divider style={{ margin: "8px 0" }} />

                <div
                  style={{ maxHeight: 180, overflowY: "auto", paddingRight: 4 }}
                >
                  {isOpenOnlyMeet ? (
                    <>
                      <div style={{ marginBottom: 4 }}>
                        <Checkbox checked disabled>
                          Open
                        </Checkbox>
                      </div>
                      <Paragraph
                        type="secondary"
                        style={{ fontSize: 11, marginTop: 4 }}
                      >
                        This meet uses Open age groups only.
                      </Paragraph>
                    </>
                  ) : ageGroupOptions.length === 0 ? (
                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                      No age groups were detected for this meet.
                    </Paragraph>
                  ) : (
                    <Checkbox.Group
                      style={{ width: "100%" }}
                      value={selectedAgeGroups}
                      onChange={(vals: any) =>
                        setSelectedAgeGroups(vals as string[])
                      }
                    >
                      {ageGroupOptions.map((opt) => (
                        <div key={opt.value} style={{ marginBottom: 4 }}>
                          <Checkbox value={opt.value}>{opt.label}</Checkbox>
                        </div>
                      ))}
                    </Checkbox.Group>
                  )}
                </div>

                <Divider style={{ margin: "16px 0" }} />
              </>
            )}
          </div>
        )}

        {context === "entries" && selectedReport === "bestTimes" && (
          <div>
            {!singleReportMode && (
              <>
                <Divider style={{ margin: "8px 0" }}>Club</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Choose club:</div>
                  <Select
                    style={{ width: "100%" }}
                    value={selectedClubId}
                    onChange={(val: any) => setSelectedClubId(val)}
                    disabled={!meetClubs || meetClubs.length === 0}
                  >
                    {meetClubs.map((c) => {
                      const isUserClub =
                        !!user && String(c.clubId) === String(user.club_id)
                      const roleName =
                        user && user.role !== undefined
                          ? (roleMapping as any)[user.role]
                          : null
                      const canSelectAllClubs =
                        roleName === "League Admin" ||
                        roleName === "Hydro Admin" ||
                        (user && user.role === 2)
                      const disabled = !canSelectAllClubs && !isUserClub
                      const label =
                        c.nameLong ||
                        c.club_name_long ||
                        c.club_name ||
                        c.name ||
                        c.clubId
                      const isSelected = String(selectedClubId) === String(c.clubId)
                      return (
                        <Option key={c.clubId} value={c.clubId} disabled={disabled}>
                          {disabled &&
                            !isUserClub &&
                            !(isSelected && !canSelectAllClubs) ? (
                            <Tooltip title="League Admins only.">
                              <span
                                style={{
                                  color: isSelected ? undefined : "#999",
                                  fontWeight: isSelected ? 600 : 400,
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                {isSelected && (
                                  <span style={{ marginRight: 6 }}>✓</span>
                                )}
                                {label}
                              </span>
                            </Tooltip>
                          ) : (
                            <span
                              style={{
                                fontWeight: isSelected ? 600 : 400,
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {isSelected && (
                                <span style={{ marginRight: 6 }}>✓</span>
                              )}
                              {label}
                            </span>
                          )}
                        </Option>
                      )
                    })}
                  </Select>
                </div>
              </>
            )}

            <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
              Best Time Matrix creates a CSV for the selected club listing each
              swimmer and their best official time in every eligible individual
              event, and, if needed, converting to the meet&apos;s course (converted
              times marked with *). Use it to compare swimmers across events and
              plan lineups strategically. Relays not included.
            </Paragraph>
          </div>
        )}

        {context === "entries" && selectedReport === "exportEntries" && (
          <div>
            <Divider style={{ margin: "8px 0" }}>Export Meet Entries File</Divider>
            <Paragraph style={{ marginTop: 8 }}>
              Export Meet Entries builds a standard entry files for this meet
              that you can send to other clubs to import into meet management
              systems. It includes all individual and relay results, swimmer
              details, and result times for the selected club(s).
            </Paragraph>
            <Divider style={{ margin: "8px 0" }}>Clubs</Divider>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>Choose clubs to include</div>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                value={exportSelectedClubIds}
                onChange={(vals: any[]) => setExportSelectedClubIds(vals)}
                disabled={!meetClubs || meetClubs.length === 0}
                maxTagCount="responsive"
              >
                {meetClubs.map((c) => {
                  const label =
                    c.nameLong ||
                    c.club_name_long ||
                    c.club_name ||
                    c.name ||
                    c.clubId
                  const isSelected = exportSelectedClubIds.some(
                    (id) => String(id) === String(c.clubId),
                  )
                  const isUserClub =
                    !!user && String(c.clubId) === String(user.club_id)
                  const roleNameLocal =
                    user && user.role !== undefined
                      ? (roleMapping as any)[user.role]
                      : null
                  const canSelectAllClubs =
                    roleNameLocal === "League Admin" ||
                    roleNameLocal === "Hydro Admin" ||
                    (user && user.role === 2)
                  const disabled = !canSelectAllClubs && !isUserClub
                  return (
                    <Option key={c.clubId} value={c.clubId} disabled={disabled}>
                      <span
                        style={{
                          fontWeight: isSelected ? 600 : 400,
                          display: "inline-flex",
                          alignItems: "center",
                          color: disabled && !isSelected ? "#999" : undefined,
                        }}
                      >
                        {isSelected && <span style={{ marginRight: 6 }}>✓</span>}
                        {label}
                      </span>
                    </Option>
                  )
                })}
              </Select>
              <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                SD3 export will include entries only for the clubs selected here.
                By default, all meet clubs are selected.
              </Paragraph>
            </div>
            <Paragraph type="secondary" style={{ fontSize: 11 }}>
              Click <strong>Open Export Meet Entries</strong> below to continue.
            </Paragraph>
          </div>
        )}

        {context === "entries" && selectedReport === "exportMeetEvents" && (
          <div>
            <Divider style={{ margin: "8px 0" }}>Export Meet Events File</Divider>
            <Paragraph style={{ marginTop: 8 }}>
              Export a zip file of your Meet Events File. The zip file contains a
              .HYV meet events file that is compatible with other team
              management solutions. Users should import the file to load the meet
              &amp; events in their team management solution, complete their
              entries, and send back their entries file to you for import under
              the actions menu for this meet. This is only necessary if the other
              team does not use the Swimmingly Clubhouse to complete their entries.
            </Paragraph>
          </div>
        )}

        {context === "results" && (
          <div>
            {selectedReport === "resultsPdf" && (
              <>
                {renderBrandingControls(
                  resultsPdfBrandingSlotIndex,
                  setResultsPdfBrandingSlotIndex,
                )}
                <Divider style={{ margin: "8px 0" }}>Layout</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Generate Results By:</div>
                  <Radio.Group
                    value={resultsPdfMode}
                    onChange={(e: any) =>
                      setResultsPdfMode(e.target.value as "event" | "heat")
                    }
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="event">Event</Radio.Button>
                    <Radio.Button value="heat">Heat</Radio.Button>
                  </Radio.Group>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Columns per page</div>
                  <Radio.Group
                    value={resultsPdfColumns}
                    onChange={(e: any) =>
                      setResultsPdfColumns(
                        e.target.value === 2 ? 2 : 1,
                      )
                    }
                  >
                    <Radio value={1}>1 column</Radio>
                    <Radio value={2}>2 columns</Radio>
                  </Radio.Group>
                </div>

                {resultsPdfMode === "event" && (
                  <Paragraph type="secondary" style={{ fontSize: 11, marginTop: -8, marginBottom: 16 }}>
                    Results are generated by event, with event place.
                  </Paragraph>
                )}

                {resultsPdfMode === "heat" && (
                  <Paragraph type="secondary" style={{ fontSize: 11, marginTop: -8, marginBottom: 16 }}>
                    Resultts are generated by heat, with heat & event place.
                  </Paragraph>
                )}

                {resultsPdfMode === "event" && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4 }}>Places to include</div>
                    <div style={{ marginBottom: 8 }}>
                      <Checkbox
                        checked={resultsPdfAllPlaces}
                        onChange={(e: any) => {
                          const checked = e.target.checked
                          setResultsPdfAllPlaces(checked)
                          if (checked) {
                            setResultsPdfMinPlace("")
                            setResultsPdfMaxPlace("")
                            setResultsPdfPlacesError(null)
                          }
                        }}
                      >
                        All places
                      </Checkbox>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        opacity: resultsPdfAllPlaces ? 0.5 : 1,
                        cursor: resultsPdfAllPlaces ? "not-allowed" : "auto",
                      }}
                    >
                      <Input
                        style={{ width: 80 }}
                        type="number"
                        min={1}
                        max={99}
                        placeholder="min"
                        value={resultsPdfMinPlace}
                        onChange={(e: any) => {
                          const val = e.target.value
                          setResultsPdfMinPlace(val)
                          if (!val && !resultsPdfMaxPlace) {
                            setResultsPdfPlacesError(null)
                            return
                          }
                          const min = val ? parseInt(val, 10) : NaN
                          const max = resultsPdfMaxPlace
                            ? parseInt(resultsPdfMaxPlace, 10)
                            : NaN
                          if (
                            (val && (Number.isNaN(min) || min < 1 || min > 99)) ||
                            (!Number.isNaN(max) && (max < 1 || max > 99))
                          ) {
                            setResultsPdfPlacesError(
                              "Places must be between 1 and 99.",
                            )
                          } else if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
                            setResultsPdfPlacesError(
                              "Min place must be less than or equal to max place.",
                            )
                          } else {
                            setResultsPdfPlacesError(null)
                          }
                        }}
                        disabled={resultsPdfAllPlaces}
                      />
                      <span style={{ lineHeight: "32px" }}>to</span>
                      <Input
                        style={{ width: 80 }}
                        type="number"
                        min={1}
                        max={99}
                        placeholder="max"
                        value={resultsPdfMaxPlace}
                        onChange={(e: any) => {
                          const val = e.target.value
                          setResultsPdfMaxPlace(val)
                          if (!val && !resultsPdfMinPlace) {
                            setResultsPdfPlacesError(null)
                            return
                          }
                          const max = val ? parseInt(val, 10) : NaN
                          const min = resultsPdfMinPlace
                            ? parseInt(resultsPdfMinPlace, 10)
                            : NaN
                          if (
                            (val && (Number.isNaN(max) || max < 1 || max > 99)) ||
                            (!Number.isNaN(min) && (min < 1 || min > 99))
                          ) {
                            setResultsPdfPlacesError(
                              "Places must be between 1 and 99.",
                            )
                          } else if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
                            setResultsPdfPlacesError(
                              "Min place must be less than or equal to max place.",
                            )
                          } else {
                            setResultsPdfPlacesError(null)
                          }
                        }}
                        disabled={resultsPdfAllPlaces}
                      />
                    </div>
                    {resultsPdfPlacesError && !resultsPdfAllPlaces && (
                      <Paragraph
                        type="danger"
                        style={{ fontSize: 11, marginTop: 4, marginBottom: 0 }}
                      >
                        {resultsPdfPlacesError}
                      </Paragraph>
                    )}
                  </div>
                )}

                {resultsPdfMode === "heat" && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4 }}>Heats to include</div>
                    <Checkbox
                      checked={resultsPdfFirstHeatOnly}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        setResultsPdfFirstHeatOnly(checked)
                        if (checked) {
                          setResultsPdfLastHeatOnly(false)
                        }
                      }}
                    >
                      Print only the first heat
                    </Checkbox>
                    <br />
                    <Checkbox
                      checked={resultsPdfLastHeatOnly}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        setResultsPdfLastHeatOnly(checked)
                        if (checked) {
                          setResultsPdfFirstHeatOnly(false)
                        }
                      }}
                    >
                      Print only the last heat
                    </Checkbox>
                  </div>
                )}

                <Divider style={{ margin: "8px 0" }}>Clubs</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>
                    Choose clubs to include
                  </div>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    value={resultsPdfClubIds}
                    onChange={(vals: any[]) => setResultsPdfClubIds(vals)}
                    disabled={!meetClubs || meetClubs.length === 0}
                    maxTagCount="responsive"
                  >
                    {meetClubs.map((c) => {
                      const label =
                        c.nameLong ||
                        c.club_name_long ||
                        c.club_name ||
                        c.name ||
                        c.clubId
                      const isSelected = resultsPdfClubIds.some(
                        (id) => String(id) === String(c.clubId),
                      )
                      return (
                        <Option key={c.clubId} value={c.clubId}>
                          <span
                            style={{
                              fontWeight: isSelected ? 600 : 400,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {isSelected && (
                              <span style={{ marginRight: 6 }}>✓</span>
                            )}
                            {label}
                          </span>
                        </Option>
                      )
                    })}
                  </Select>
                </div>

                {Array.isArray(meetEventsArr) && meetEventsArr.length > 0 && (
                  <>
                    <Divider style={{ margin: "8px 0" }}>Events</Divider>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 4 }}>
                        Choose events to include
                      </div>
                      <Select
                        mode="multiple"
                        style={{ width: "100%" }}
                        value={resultsPdfEventIds}
                        onChange={(vals: any[]) =>
                          setResultsPdfEventIds(vals)
                        }
                        maxTagCount="responsive"
                      >
                        {(meetEventsArr as any[]).map((ev) => {
                          const id =
                            ev.id != null
                              ? ev.id
                              : ev.swimEventId != null
                                ? ev.swimEventId
                                : null
                          if (id == null) return null

                          const eventNumber = resolveEventNumber(
                            ev.displayNumber || ev.display_number,
                            ev.event_sort_order || ev.eventNumber || ev.event_number,
                          )
                          const gender = ev.gender || ""
                          const ageGroup = ev.age_group || ""
                          const distance = ev.distance || ""
                          const stroke =
                            ev.stroke ||
                            ev.stroke_name ||
                            ev.long_name ||
                            ""
                          const division = ev.division || ""

                          const parts = [
                            division,
                            gender,
                            ageGroup,
                            distance,
                            stroke,
                          ]
                            .map((x) => String(x || "").trim())
                            .filter(Boolean)

                          const baseLabel = parts.join(" ") || id
                          const label = eventNumber
                            ? `Event ${eventNumber}: ${baseLabel}`
                            : String(baseLabel)

                          const isSelected = resultsPdfEventIds.some(
                            (val) => String(val) === String(id),
                          )

                          return (
                            <Option key={id} value={id}>
                              <span
                                style={{
                                  fontWeight: isSelected ? 600 : 400,
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                {isSelected && (
                                  <span style={{ marginRight: 6 }}>✓</span>
                                )}
                                {label}
                              </span>
                            </Option>
                          )
                        })}
                      </Select>
                    </div>
                  </>
                )}

                <Divider style={{ margin: "8px 0" }}>Options</Divider>

                <div style={{ marginBottom: 8 }}>
                  <Tooltip
                    title="Show team scores in the header above the event results."
                  >
                    <Checkbox
                      checked={resultsPdfShowTeamScores}
                      onChange={(e: any) =>
                        setResultsPdfShowTeamScores(e.target.checked)
                      }
                    >
                      Show Team Scores
                    </Checkbox>
                  </Tooltip>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Tooltip
                    title="Show points earned by each swimmer and relay in the results."
                  >
                    <Checkbox
                      checked={resultsPdfShowSwimPoints}
                      onChange={(e: any) =>
                        setResultsPdfShowSwimPoints(e.target.checked)
                      }
                    >
                      Show Swimmer & Relay Points
                    </Checkbox>
                  </Tooltip>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Tooltip title="Show disqualifications (DQs) in each event.">
                    <Checkbox
                      checked={resultsPdfShowDqs}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        setResultsPdfShowDqs(checked)
                        if (!checked) {
                          setResultsPdfShowDqTimes(false)
                          setResultsPdfShowDqReasons(false)
                        }
                      }}
                    >
                      Show DQs in Each Event
                    </Checkbox>
                  </Tooltip>
                </div>

                {resultsPdfShowDqs && (
                  <div
                    style={{
                      marginBottom: 8,
                      marginLeft: 24,
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <Tooltip title="Show the times for any DQ, even though they are disqualified and the time is not counted.">
                      <Checkbox
                        checked={resultsPdfShowDqTimes}
                        onChange={(e: any) =>
                          setResultsPdfShowDqTimes(e.target.checked)
                        }
                      >
                        Show DQ Record Times
                      </Checkbox>
                    </Tooltip>
                    <Tooltip title="Show any infraction reason(s) for DQs (recommended).">
                      <Checkbox
                        checked={resultsPdfShowDqReasons}
                        onChange={(e: any) =>
                          setResultsPdfShowDqReasons(e.target.checked)
                        }
                      >
                        Show DQ Infraction Reasons
                      </Checkbox>
                    </Tooltip>
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <Checkbox
                    checked={resultsPdfShowExhibition}
                    onChange={(e: any) =>
                      setResultsPdfShowExhibition(e.target.checked)
                    }
                  >
                    Include Exhibition Results
                  </Checkbox>
                </div>
              </>
            )}

            {selectedReport === "ribbonLabelsPdf" && (
              <>
                <Divider style={{ margin: "8px 0" }}>Layout</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 4 }}>Starting label position</div>
                  <RibbonLabelsStartGrid
                    value={(() => {
                      const n = ribbonLabelsStartLabel
                        ? parseInt(ribbonLabelsStartLabel, 10)
                        : NaN
                      return Number.isNaN(n) || n < 1 || n > 30 ? 1 : n
                    })()}
                    onChange={(val) => {
                      setRibbonLabelsStartLabel(String(val))
                      setRibbonLabelsStartLabelError(null)
                    }}
                  />
                  <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                    Use this when printing on a partially-used Avery 5160 sheet.
                    For example, enter 16 to begin on the 16th label of the first
                    page and fill every label after that.
                  </Paragraph>
                  {ribbonLabelsStartLabelError && (
                    <Paragraph
                      type="danger"
                      style={{ fontSize: 11, marginTop: 4, marginBottom: 0 }}
                    >
                      {ribbonLabelsStartLabelError}
                    </Paragraph>
                  )}
                </div>

                <Divider style={{ margin: "8px 0" }}>Sort & Filters</Divider>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 4 }}>Sort ribbons</div>
                  <Radio.Group
                    value={ribbonLabelsSortMode}
                    onChange={(e: any) =>
                      setRibbonLabelsSortMode(e.target.value as
                        | "event"
                        | "swimmer"
                        | "ageGroup")
                    }
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="event">By Event</Radio.Button>
                    <Radio.Button value="swimmer">By Swimmer</Radio.Button>
                    <Radio.Button value="ageGroup">By Age Group</Radio.Button>
                  </Radio.Group>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      color: "#595959",
                      lineHeight: 1.4,
                    }}
                  >
                    <div>
                      <strong>By Event</strong>: event order, then place/time within each event.
                    </div>
                    <div>
                      <strong>By Swimmer</strong>: swimmer name (last, first), then event order.
                    </div>
                    <div>
                      <strong>By Age Group</strong>: age group, then gender, swimmer, and event.
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      marginBottom: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>Events to include</span>
                  </div>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="All events"
                    value={ribbonLabelsEventIds}
                    maxTagCount={0}
                    maxTagPlaceholder={() => {
                      const totalEvents = Array.isArray(meetEventsArr)
                        ? meetEventsArr.length
                        : 0
                      const selectedCount = ribbonLabelsEventIds.length

                      if (!selectedCount) {
                        return "All events"
                      }
                      if (totalEvents > 0 && selectedCount === totalEvents) {
                        return "All Events Selected"
                      }
                      return `${selectedCount} of ${totalEvents} Events Selected`
                    }}
                    onChange={(vals: Array<number | string>) =>
                      setRibbonLabelsEventIds(vals)
                    }
                    popupRender={(menu) => (
                      <div>
                        {Array.isArray(meetEventsArr) &&
                          meetEventsArr.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 8,
                                padding: "4px 8px",
                                fontSize: 11,
                              }}
                            >
                              <Button
                                type="link"
                                size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  const allEventIds: Array<number | string> = []
                                    ; (meetEventsArr as any[]).forEach((ev) => {
                                      const id =
                                        ev.id != null
                                          ? ev.id
                                          : ev.swimEventId != null
                                            ? ev.swimEventId
                                            : null
                                      if (id != null) {
                                        allEventIds.push(id)
                                      }
                                    })
                                  if (allEventIds.length > 0) {
                                    setRibbonLabelsEventIds(allEventIds)
                                  }
                                }}
                              >
                                Select all
                              </Button>
                              <Button
                                type="link"
                                size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setRibbonLabelsEventIds([])
                                }}
                              >
                                Deselect all
                              </Button>
                            </div>
                          )}
                        {menu}
                      </div>
                    )}
                  >
                    {(meetEventsArr || []).map((ev: any) => {
                      const id =
                        ev.id != null
                          ? ev.id
                          : ev.swimEventId != null
                            ? ev.swimEventId
                            : null
                      if (id == null) return null

                      const courseLabel =
                        ev && ev.unit_of_measure != null
                          ? convertCourse[ev.unit_of_measure] || ""
                          : ""
                      const eventNumber = resolveEventNumber(
                        ev.displayNumber || ev.display_number,
                        ev.event_sort_order || ev.eventNumber || ev.event_number,
                      )

                      const stroke =
                        ev.stroke ||
                        ev.stroke_name ||
                        ev.long_name ||
                        ""
                      const divisionLabel = ev.division ? ` [${ev.division}]` : ""

                      return (
                        <Option key={id} value={id}>
                          {`Event ${eventNumber}${divisionLabel}: ${ev.age_group} ${ev.gender} ${ev.distance} ${courseLabel} ${stroke}`}
                        </Option>
                      )
                    })}
                  </Select>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 4 }}>Clubs to include</div>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    value={ribbonLabelsClubIds}
                    disabled={!meetClubs || meetClubs.length === 0}
                    maxTagCount={0}
                    maxTagPlaceholder={() => {
                      const totalClubs = Array.isArray(meetClubs)
                        ? meetClubs.length
                        : 0
                      const selectedCount = ribbonLabelsClubIds.length

                      if (!selectedCount) {
                        return "All clubs"
                      }
                      if (totalClubs > 0 && selectedCount === totalClubs) {
                        return "All Clubs Selected"
                      }
                      return `${selectedCount} of ${totalClubs} Clubs Selected`
                    }}
                    onChange={(vals: Array<number | string>) =>
                      setRibbonLabelsClubIds(vals)
                    }
                  >
                    {meetClubs.map((c) => {
                      const label =
                        c.nameLong ||
                        c.club_name_long ||
                        c.club_name ||
                        c.name ||
                        c.clubId
                      const isSelected = ribbonLabelsClubIds.some(
                        (id) => String(id) === String(c.clubId),
                      )
                      return (
                        <Option key={c.clubId} value={c.clubId}>
                          <span
                            style={{
                              fontWeight: isSelected ? 600 : 400,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {isSelected && (
                              <span style={{ marginRight: 6 }}>✓</span>
                            )}
                            {label}
                          </span>
                        </Option>
                      )
                    })}
                  </Select>
                </div>

                {(ribbonLabelsSortMode === "swimmer" ||
                  ribbonLabelsSortMode === "ageGroup") && (
                    <div style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          marginBottom: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span>Swimmers to include</span>
                      </div>
                      <Select
                        mode="multiple"
                        style={{ width: "100%" }}
                        placeholder="All swimmers"
                        value={ribbonLabelsSwimmerIds}
                        onChange={(vals: Array<number | string>) => {
                          const ids = vals
                            .map((v) => parseInt(String(v), 10))
                            .filter((id) => Number.isFinite(id))
                          setRibbonLabelsSwimmerIds(ids)
                        }}
                        maxTagCount={0}
                        maxTagPlaceholder={() => {
                          const total = ribbonLabelsSwimmerOptions.length
                          const selected = ribbonLabelsSwimmerIds.length
                          if (!total) return "No swimmers"
                          if (!selected || selected === total) {
                            return "All swimmers"
                          }
                          return `${selected} of ${total} Swimmers Selected`
                        }}
                        popupRender={(menu) => (
                          <div>
                            {ribbonLabelsSwimmerOptions.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 8,
                                  padding: "4px 8px",
                                  fontSize: 11,
                                }}
                              >
                                <Button
                                  type="link"
                                  size="small"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    const allIds = ribbonLabelsSwimmerOptions
                                      .map((s) =>
                                        s && s.swimmerId != null
                                          ? Number(s.swimmerId)
                                          : null,
                                      )
                                      .filter(
                                        (id): id is number =>
                                          typeof id === "number" && !Number.isNaN(id),
                                      )
                                    setRibbonLabelsSwimmerIds(allIds)
                                  }}
                                >
                                  Select all
                                </Button>
                                <Button
                                  type="link"
                                  size="small"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setRibbonLabelsSwimmerIds([])
                                  }}
                                >
                                  Deselect all
                                </Button>
                              </div>
                            )}
                            {menu}
                          </div>
                        )}
                      >
                        {(() => {
                          const groups: {
                            [key: string]: typeof ribbonLabelsSwimmerOptions
                          } = {}

                          ribbonLabelsSwimmerOptions.forEach((s) => {
                            const clubLabel = s.clubCode || "Club"
                            const genderLabel = s.gender || "All"
                            const ageLabel = s.ageGroupLabel || "All Ages"
                            const groupKey = `${clubLabel} | ${genderLabel} | ${ageLabel}`
                            if (!groups[groupKey]) {
                              groups[groupKey] = []
                            }
                            groups[groupKey].push(s)
                          })

                          return Object.keys(groups).map((groupKey) => (
                            <OptGroup key={groupKey} label={groupKey}>
                              {groups[groupKey].map((s) => {
                                const name = `${s.lastName || ""}, ${s.firstName || ""
                                  }`.trim()
                                return (
                                  <Option
                                    key={s.swimmerId}
                                    value={s.swimmerId}
                                  >
                                    {name}
                                  </Option>
                                )
                              })}
                            </OptGroup>
                          ))
                        })()}
                      </Select>
                    </div>
                  )}

                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 4 }}>Age groups & gender</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ minWidth: 180, flex: 1 }}
                      placeholder="All age groups"
                      value={selectedAgeGroups}
                      onChange={(vals: string[]) => setSelectedAgeGroups(vals)}
                    >
                      {ageGroupOptions.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                    <Select
                      style={{ width: 140 }}
                      value={genderFilter}
                      onChange={(val: "both" | "girls" | "boys") =>
                        setGenderFilter(val)
                      }
                    >
                      <Option value="both">Girls &amp; Boys</Option>
                      <Option value="girls">Girls only</Option>
                      <Option value="boys">Boys only</Option>
                    </Select>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 4 }}>Places to include</div>
                  <div
                    style={{
                      display: "flex",
                      gap: 24,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Column 1: All places, points, exhibition */}
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <Checkbox
                          checked={ribbonLabelsAllPlaces}
                          disabled={ribbonLabelsDqMode}
                          onChange={(e: any) => {
                            const checked = e.target.checked
                            setRibbonLabelsAllPlaces(checked)
                            if (checked) {
                              setRibbonLabelsMinPlace(null)
                              setRibbonLabelsMaxPlace(null)
                              setRibbonLabelsPlacesError(null)
                            } else {
                              // Default range when user first opts into filtering
                              setRibbonLabelsMinPlace(1)
                              setRibbonLabelsMaxPlace(99)
                              setRibbonLabelsPlacesError(null)
                            }
                          }}
                        >
                          All official places
                        </Checkbox>
                      </div>

                      {!ribbonLabelsAllPlaces && !ribbonLabelsDqMode && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ fontSize: 12 }}>from</span>
                          <InputNumber
                            style={{ width: 70 }}
                            size="small"
                            min={1}
                            max={99}
                            value={ribbonLabelsMinPlace as number | null}
                            onChange={(val) => {
                              const min = typeof val === "number" ? val : null
                              setRibbonLabelsMinPlace(min)
                              const max = ribbonLabelsMaxPlace
                              if (min != null && max != null && min > max) {
                                setRibbonLabelsPlacesError(
                                  "Min place must be less than or equal to max place.",
                                )
                              } else {
                                setRibbonLabelsPlacesError(null)
                              }
                            }}
                          />
                          <span style={{ fontSize: 12 }}>to</span>
                          <InputNumber
                            style={{ width: 70 }}
                            size="small"
                            min={1}
                            max={99}
                            value={ribbonLabelsMaxPlace as number | null}
                            onChange={(val) => {
                              const max = typeof val === "number" ? val : null
                              setRibbonLabelsMaxPlace(max)
                              const min = ribbonLabelsMinPlace
                              if (min != null && max != null && min > max) {
                                setRibbonLabelsPlacesError(
                                  "Max place must be greater than or equal to min place.",
                                )
                              } else {
                                setRibbonLabelsPlacesError(null)
                              }
                            }}
                          />
                        </div>
                      )}

                      {ribbonLabelsPlacesError && !ribbonLabelsDqMode && (
                        <Paragraph
                          type="danger"
                          style={{
                            fontSize: 11,
                            marginTop: 4,
                            marginBottom: 0,
                          }}
                        >
                          {ribbonLabelsPlacesError}
                        </Paragraph>
                      )}
                      {ribbonLabelsAllPlaces && !ribbonLabelsDqMode && (
                        <Paragraph
                          type="secondary"
                          style={{
                            fontSize: 11,
                            marginTop: 4,
                            marginBottom: 0,
                          }}
                        >
                          Place filters apply only to official, non‑exhibition
                          swims.
                        </Paragraph>
                      )}
                      {ribbonLabelsDqMode && (
                        <Paragraph
                          type="secondary"
                          style={{
                            fontSize: 11,
                            marginTop: 4,
                            marginBottom: 0,
                          }}
                        >
                          In DQ‑only mode, official places are ignored and place
                          filters are disabled.
                        </Paragraph>
                      )}

                      <div style={{ marginTop: 12 }}>
                        <div style={{ marginBottom: 4 }}>Place shown on label</div>
                        <Radio.Group
                          size="small"
                          value={ribbonLabelsPlaceMode}
                          onChange={(e: any) =>
                            setRibbonLabelsPlaceMode(e.target.value)
                          }
                          disabled={ribbonLabelsDqMode}
                        >
                          <Radio.Button value="event">Event Place</Radio.Button>
                          <Radio.Button value="heat">Heat Place</Radio.Button>
                        </Radio.Group>
                      </div>

                      {ribbonLabelsPlaceMode === "event" && !ribbonLabelsDqMode && (
                        <div style={{ marginTop: 8 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <Checkbox
                              checked={ribbonLabelsPlaceCapEnabled}
                              onChange={(e: any) => {
                                const checked = e.target.checked
                                setRibbonLabelsPlaceCapEnabled(checked)
                                if (!checked) {
                                  setRibbonLabelsPlaceCap(null)
                                }
                              }}
                            >
                              Only show place up to
                            </Checkbox>
                            {ribbonLabelsPlaceCapEnabled && (
                              <>
                                <InputNumber
                                  style={{ width: 70 }}
                                  size="small"
                                  min={1}
                                  max={99}
                                  value={ribbonLabelsPlaceCap as number | null}
                                  onChange={(val) => {
                                    const num =
                                      typeof val === "number" ? val : null
                                    setRibbonLabelsPlaceCap(num)
                                  }}
                                />
                                <span style={{ fontSize: 12 }}>place</span>
                              </>
                            )}
                          </div>
                          <Paragraph
                            type="secondary"
                            style={{ fontSize: 11, marginTop: 4, marginBottom: 0 }}
                          >
                            Any place after the desired place will not show a place
                            at all (participant ribbon).
                          </Paragraph>
                        </div>
                      )}

                      <div style={{ marginTop: 8, marginBottom: 4 }}>
                        <Checkbox
                          checked={ribbonLabelsShowPoints}
                          disabled={ribbonLabelsDqMode}
                          onChange={(e: any) =>
                            setRibbonLabelsShowPoints(e.target.checked)
                          }
                        >
                          Show individual points on official ribbons
                        </Checkbox>
                      </div>

                      <div style={{ marginBottom: 4 }}>
                        <Checkbox
                          checked={ribbonLabelsIncludeExhibition}
                          disabled={ribbonLabelsDqMode}
                          onChange={(e: any) =>
                            setRibbonLabelsIncludeExhibition(e.target.checked)
                          }
                        >
                          Include exhibition ribbons (printed after official)
                        </Checkbox>
                      </div>
                    </div>

                    {/* Column 2: DQ-only and DQ details */}
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ marginBottom: 4 }}>
                        <Checkbox
                          checked={ribbonLabelsDqMode}
                          onChange={(e: any) => {
                            const checked = e.target.checked
                            setRibbonLabelsDqMode(checked)
                            if (checked) {
                              // In DQ‑only mode we ignore place filters, exhibition, and points.
                              setRibbonLabelsIncludeExhibition(false)
                              setRibbonLabelsShowPoints(false)
                              setRibbonLabelsAllPlaces(false)
                              setRibbonLabelsMinPlace(null)
                              setRibbonLabelsMaxPlace(null)
                              setRibbonLabelsPlacesError(null)
                            } else {
                              // When leaving DQ‑only mode, default back to all places.
                              setRibbonLabelsAllPlaces(true)
                            }
                          }}
                        >
                          DQ‑only ribbons
                        </Checkbox>
                      </div>
                      <Paragraph
                        type="secondary"
                        style={{ fontSize: 11, marginTop: 0, marginBottom: 4 }}
                      >
                        When enabled, only DQ swims are printed. Place filters,
                        exhibition ribbons, and points are ignored.
                      </Paragraph>

                      {ribbonLabelsDqMode && (
                        <>
                          <div style={{ marginBottom: 4 }}>
                            <Checkbox
                              checked={ribbonLabelsShowDqTimes}
                              onChange={(e: any) =>
                                setRibbonLabelsShowDqTimes(e.target.checked)
                              }
                            >
                              Show DQ record times on DQ ribbons
                            </Checkbox>
                          </div>

                          <div style={{ marginBottom: 4 }}>
                            <Checkbox
                              checked={ribbonLabelsShowDqReasons}
                              onChange={(e: any) =>
                                setRibbonLabelsShowDqReasons(e.target.checked)
                              }
                            >
                              Show DQ infraction reasons on DQ ribbons
                            </Checkbox>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedReport === "resultsExcel" && (
              <>
                <Divider style={{ margin: "8px 0" }}>
                  About Results Spreadsheet
                </Divider>
                <Paragraph style={{ marginTop: 8 }}>
                  The spreadsheet has 3 tabs: Team Scores, Swimmer Results, and
                  High Point scoring. The swimmer results contain the raw data
                  for each record, including DQ infractions &amp; time
                  improvement. High point tracking is sorted by age group and
                  ranked for your convenience.
                </Paragraph>
              </>
            )}

            {selectedReport === "resultsExport" && (
              <>
                <Divider style={{ margin: "8px 0" }}>
                  About Export Meet Results
                </Divider>
                <Paragraph style={{ marginTop: 8 }}>
                  Export Meet Results builds standardized results files in universally compatible formats (HY3/CL2/SD3) for this
                  meet that you can send to leagues, host clubs, or other meet
                  management systems. It includes final times, places, and
                  scoring for all clubs included in the export and does not
                  change any data inside Swimmingly.
                </Paragraph>
              </>
            )}

            {(selectedReport === "resultsPdfLegacy" ||
              selectedReport === "labelsPdfLegacy") && (
                <>
                  <Divider style={{ margin: "16px 0 8px" }}>
                    About Legacy PDFs
                  </Divider>
                  <Paragraph style={{ marginTop: 8 }}>
                    The legacy Results and Labels PDFs are generated and uploaded by
                    the Swimmingly timing apps after a meet. Use these actions to
                    download the existing files. If no legacy PDF exists yet, run
                    the post-meet upload from the timing app or regenerate from
                    mobile before returning here.
                  </Paragraph>
                  <Paragraph type="secondary" style={{ fontSize: 11 }}>
                    A new server-side PDF engine will be added later; for now these
                    downloads reflect exactly what was produced on the timing
                    device.
                  </Paragraph>
                </>
              )}
          </div>
        )}
      </Col>
      {!isMobile && (
        <Col xs={24} md={9}>
          <Divider style={{ margin: "8px 0" }}>Preview</Divider>
          {context === "results" && selectedReport === "resultsExcel" ? (
            renderResultsExcelPreview()
          ) : context === "results" && selectedReport === "resultsExport" ? (
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              Preview is not available for meet results export. After you
              generate the file, you can import it into other team management
              software to review detailed results and scoring.
            </Paragraph>
          ) : context === "entries" && selectedReport === "bestTimes" ? (
            renderBestTimesPreview()
          ) : context === "entries" && selectedReport === "exportEntries" ? (
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              Export Meet Entries opens a separate dialog; there is no PDF
              preview for this action.
            </Paragraph>
          ) : (
            renderPreviewContent()
          )}
        </Col>
      )}
    </Row>
  )

  return (
    <>
      {inline ? (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: 12,
                opacity: 0.75,
              }}
            >
              Choose a report, adjust options, then generate & download.
            </div>
          </div>
          {mainContent}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <Button
              type="primary"
              onClick={handleSecondaryFooterClick}
              loading={downloadLoading}
            >
              {getCreateReportLabel()}
            </Button>
          </div>
        </div>
      ) : (
        <Modal
          open={visible}
          onCancel={onClose}
          width={isMobile ? "100%" : 1040}
          styles={{
            body: {
              maxHeight: isMobile ? "calc(100vh - 160px)" : "70vh",
              overflowY: "auto",
            },
          }}
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Button onClick={onClose}>Close</Button>
              <div style={{ display: "flex", gap: 8 }}>{renderSecondaryFooterButton()}</div>
            </div>
          }
          title={
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                Swim Meet Report Studio
              </div>
              <div style={{ fontSize: 13 }}>{meetTitle}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {[courseLabel, dateLabel].filter(Boolean).join(" ") ||
                  "Choose a report to generate for this swim meet."}
              </div>
            </div>
          }
        >
          {mainContent}
        </Modal>
      )}

      {isMobile && (
        <Drawer
          placement="bottom"
          closable
          onClose={() => setPreviewDrawerVisible(false)}
          open={previewDrawerVisible}
          size="80%"
          title={getCreateReportLabel()}
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <Button onClick={() => setPreviewDrawerVisible(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={handlePreviewDrawerDownload}
                loading={previewDrawerDownloadLoading}
                disabled={!isPreviewableSelection}
              >
                Download
              </Button>
            </div>
          }
        >
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>{meetTitle}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {[courseLabel, dateLabel].filter(Boolean).join(" ")}
            </div>
          </div>
          {context === "results" && selectedReport === "resultsExcel"
            ? renderResultsExcelPreview()
            : context === "entries" && selectedReport === "bestTimes"
              ? renderBestTimesPreview()
              : renderPreviewContent()}
        </Drawer>
      )}

      <Modal
        open={exportEventsModalVisible}
        onCancel={() => setExportEventsModalVisible(false)}
        footer={null}
        title="Export Meet Events"
      >
        <Typography.Paragraph style={{ fontSize: 12, marginBottom: 12 }}>
          Choose how you&apos;d like to export this meet&apos;s event order.
        </Typography.Paragraph>
        <Space orientation="vertical" style={{ width: "100%" }} size={12}>
          <div>
            <Button
              type="primary"
              block
              onClick={() => {
                setExportEventsModalVisible(false)
                setHyvModalVisible(true)
              }}
            >
              EV3/HYV Events File
            </Button>
            <Typography.Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
              Download an EV3 or HYV events file compatible with Hy-Tek and other team
              management systems.
            </Typography.Paragraph>
          </div>
          <div>
            <Button
              block
              loading={exportEventsCsvLoading}
              onClick={async () => {
                if (!meetId) return
                try {
                  setExportEventsCsvLoading(true)
                  const data: any = await (swimminglyApi as any).get(
                    gen(`/api/getMeetEventOrder/${meetId}`),
                  )
                  const events: any[] = data?.meetEvents || []
                  if (events.length === 0) {
                    message.error("No event order is available to export.")
                    return
                  }
                  const escapeCell = (val: unknown): string => {
                    if (val === null || val === undefined) return ""
                    const str = String(val)
                    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`
                    return str
                  }
                  const hasDivision = events.some((evt: any) => !!evt.division)
                  const header = ["Event #", ...(hasDivision ? ["Division"] : []), "Age Group", "Gender", "Distance", "Stroke"]
                  const rows = events.map((evt: any) => {
                    const ag = String(evt.ageGroup ?? "")
                    const ageGroupCell = /^\d+\s*-\s*\d+$/.test(ag)
                      ? `="${ag.replace(/"/g, '""')}"`
                      : escapeCell(ag)
                    const evNum = resolveEventNumber(evt.displayNumber, evt.eventNumber)
                    return [
                      escapeCell(evNum),
                      ...(hasDivision ? [escapeCell(evt.division)] : []),
                      ageGroupCell,
                      escapeCell(evt.gender),
                      escapeCell(evt.distance),
                      escapeCell(evt.stroke),
                    ]
                  })
                  const csv = [header.join(","), ...rows.map((r: string[]) => r.join(","))].join("\n") + "\n"
                  const safeName = (meetTitle || "Meet").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
                  saveFile(csv, `${safeName || "Meet"}__EventOrder.csv`)
                  setExportEventsModalVisible(false)
                } catch {
                  message.error("There was a problem exporting the CSV.")
                } finally {
                  setExportEventsCsvLoading(false)
                }
              }}
            >
              CSV Events File
            </Button>
            <Typography.Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
              Download a CSV of the event order (event #, age group, gender, distance, stroke)
              for spreadsheets and quick review.
            </Typography.Paragraph>
          </div>
        </Space>
      </Modal>

      <Modal
        open={hyvModalVisible}
        title="Meet Events Setup"
        onCancel={() => setHyvModalVisible(false)}
        onOk={handleConfirmHyvExport}
        okText="Export Meet Events File"
        cancelText="Cancel"
        destroyOnHidden
        width={isMobile ? "100%" : 600}
      >
        <Divider style={{ margin: "8px 0" }}>Meet Details</Divider>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>Meet Name</div>
          <Input value={meetTitle} disabled />
        </div>
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>Age-up Date</div>
            <DatePicker
              style={{ width: "100%" }}
              value={hyvAgeUpDate}
              onChange={(d) => setHyvAgeUpDate(d)}
              format="MM/DD/YYYY"
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>Start Date</div>
            <Input
              value={
                meetInfoArr && meetInfoArr.meet_date
                  ? moment.utc(meetInfoArr.meet_date).format("MM/DD/YYYY")
                  : ""
              }
              disabled
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>End Date</div>
            <DatePicker
              style={{ width: "100%" }}
              value={hyvEndDate}
              onChange={(d) => setHyvEndDate(d)}
              format="MM/DD/YYYY"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>Course</div>
            <Input value={courseLabel || ""} disabled />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ marginBottom: 4 }}>Team / Facility</div>
            <Input
              value={hyvFacilityName}
              onChange={(e) => setHyvFacilityName(e.target.value)}
              placeholder="Enter team or facility name"
            />
          </div>
        </div>

        <Divider style={{ margin: "8px 0" }}>File Format</Divider>
        <div style={{ marginBottom: 12 }}>
          <Radio.Group
            value={hyvFormat}
            onChange={(e) => setHyvFormat(e.target.value as "ev3" | "hyv" | "both")}
          >
            <Radio value="ev3">EV3 only</Radio>
            <Radio value="hyv">HYV only</Radio>
            <Radio value="both">Both</Radio>
          </Radio.Group>
        </div>

        {(hyvFormat === "ev3" || hyvFormat === "both") && (
          <div style={{ marginBottom: 12 }}>
            <Checkbox
              checked={ev3LimitsEnabled}
              onChange={(e) => setEv3LimitsEnabled(e.target.checked)}
            >
              Set Entry Limits in the File
            </Checkbox>
            {hyvFormat === "both" && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 2 }}>
                Entry limits are written to the EV3 file only — not included in HYV.
              </Typography.Text>
            )}
            {ev3LimitsEnabled && (
              <div style={{ marginTop: 10 }}>
                {(
                  [
                    { label: "Individual", value: ev3IndLimit, setter: setEv3IndLimit, savedValue: meetInfoArr?.individualLimit ?? null },
                    { label: "Relay", value: ev3RelayLimit, setter: setEv3RelayLimit, savedValue: meetInfoArr?.relayLimit ?? null },
                    { label: "Total", value: ev3TotalLimit, setter: setEv3TotalLimit, savedValue: meetInfoArr?.totalLimit ?? null },
                  ] as const
                ).map(({ label, value, setter, savedValue }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}
                  >
                    <span style={{ width: 70 }}>{label}</span>
                    <InputNumber
                      min={1}
                      max={99}
                      value={value ?? undefined}
                      disabled={value === null}
                      onChange={(v) => setter(typeof v === "number" ? v : null)}
                      style={{ width: 70 }}
                    />
                    <Checkbox
                      checked={value === null}
                      onChange={(e) =>
                        setter(e.target.checked ? null : (savedValue ?? 1))
                      }
                    >
                      No Limit
                    </Checkbox>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(() => {
          const courseLabelUpper = (courseLabel || "").toUpperCase()
          const isLCM = courseLabelUpper.includes("LCM") || courseLabelUpper === "L"
          if (!isLCM) return null
          return (
            <>
              <Divider style={{ margin: "8px 0" }}>LCM Conversion</Divider>
              <div style={{ marginBottom: 8 }}>
                <Radio.Group
                  value={hyvConversionCode}
                  onChange={(e) =>
                    setHyvConversionCode(e.target.value as "CN" | "CY")
                  }
                >
                  <Radio value="CY">
                    Allow SCM/Yards entry times to convert to LCM (CY)
                  </Radio>
                  <Radio value="CN">
                    Do not convert SCM/Yards entry times to LCM (CN)
                  </Radio>
                </Radio.Group>
              </div>
            </>
          )
        })()}
      </Modal>
    </>
  )
}

export default SwimMeetReportStudio
